// @ts-nocheck
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { getDisplayName } from "@/lib/utils/display-name";
import { useStickyState } from "@/hooks/useStickyState";
import { format, addDays } from "date-fns";

type QAMessage = {
  id: string;
  role: "user" | "ai";
  text: string;
};
import { Calendar } from "@/components/ui/calendar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Play, Pause, Search, Copy, Share2, Eye, Clock, ChevronDown,
  Sparkles, FileText, Mail, ListChecks, Video, Send, Pencil, Loader2,
  CheckCircle2, Circle, ArrowUp, ArrowRight, ArrowDown, ArrowLeft,
  MessageSquare, Calendar as CalendarIcon, Users as UsersIcon,
  SkipBack, SkipForward, Volume2, VolumeX, Scissors, Check, X,
  RotateCcw, RotateCw, Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ShareRecordDialog from "@/components/shared/ShareRecordDialog";
import { MutationGate, useCanMutate, useCanShareMeeting, useCanUseMeetingCapture, READ_ONLY_MEETINGS_REASON } from "@/lib/rbac";
import { useRecordSharingEnabled } from "@/lib/hooks/use-record-sharing";
import UserAvatar from "@/components/shared/Avatar";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTasks } from "@/contexts/TasksContext";
import { people, currentUser, workspaceMembers, companies as allCompanies, deals as allDeals, meetings as allMeetings } from "@/data/dummy";
import LinkedText from "@/components/shared/LinkedText";
import NotesTabContent from "@/components/shared/NotesTabContent";
import { MeetingNotRecorded, MeetingTranscriptIncompleteNote } from "@/components/shared/MeetingNotRecorded";
import SendFollowUpEmailButton from "@/components/shared/SendFollowUpEmailButton";
import { useGetEngagementLazyQuery, useGetWorkspaceSummaryTemplatesQuery, useGetNotesForEngagementLazyQuery, useUpdateNoteMutation, useUpdateEngagementSummaryMutation, useCreateNoteMutation, NotePriorityEnum, useShareEngagementMutation, useGetWorkspaceIntelligenceQuery, useUpdateUserMutation } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import { isLovablePreview, localizeMediaUrl } from "@/lib/utils/environment";
import { getFollowUpRecipientDetails } from "@/lib/utils/follow-up-recipients";
import { blocksToPlainText } from "@/lib/utils/blocknote-plain-text";
import { getEmailDraft, meetingFollowUpDraftKey, updateEmailDraftBody } from "@/lib/utils/email-drafts";
import { useClips, CLIP_STATUS, type Clip } from "@/lib/hooks/use-clips";

import type { PartialBlock } from "@blocknote/core";
import NoteEditor from "@/components/shared/NoteEditor";
import { aiNotesToBlocks, blocksToAiNotesSections, type AiNotesBullet, type AiNotesSection } from "@/lib/utils/ai-notes";
import { summaryToBlocks, summaryPointsToBlocks, blocksToSummarySections, blocksToLegacySummarySections, type SummarySection } from "@/lib/utils/meeting-summary";
import { useKeyedDebouncedSave } from "@/lib/hooks/use-keyed-debounced-save";
import { useFeatureFlag } from "@/lib/feature-flags/hooks";
import { FEATURE_FLAGS } from "@/lib/feature-flags/registry";
import { useChatAgent } from "@/lib/hooks/use-chat-agent";
import { ChatContext } from "@/lib/types/chat";
import TaskDetailSheet from "@/components/shared/TaskDetailSheet";
import PersonDetailSheet from "@/components/shared/PersonDetailSheet";
import CompanyDetailSheet from "@/components/shared/CompanyDetailSheet";
import AskCoffeePanel from "@/components/shared/AskCoffeePanel";
import EngagementPrivacyGate from "@/components/shared/EngagementPrivacyGate";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";
import { useFormatters } from "@/lib/i18n/useFormatters";
import zoomMeetingFrame from "@/assets/zoom-meeting-frame.jpg";

interface MeetingIntelligenceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note: {
    id: string;
    name: string;
    company: string;
    people: string[];
    date: string;
  } | null;
  defaultTab?: string;
  hideOverlay?: boolean; // Hide overlay when opened as a nested/stacked sheet
  onBack?: () => void; // Back arrow handler when opened from a drill-down card
}

/* ── Dummy data ────────────────────────────── */

const transcript = [
  { speaker: currentUser.name, time: "0:00", text: "Alright, thanks for joining everyone. Let's get started with the agenda." },
  { speaker: "Sarah Chen", time: "0:12", text: `Thanks ${currentUser.name}. I wanted to walk through the migration timeline we discussed last week.` },
  { speaker: currentUser.name, time: "0:28", text: "Great, let's hear it. How are things looking on your end?" },
  { speaker: "Sarah Chen", time: "0:35", text: "We've completed the initial assessment. The data migration should take about two weeks, and then we'll need another week for testing and UAT." },
  { speaker: currentUser.name, time: "1:02", text: "That's faster than I expected. Any risks we should be aware of?" },
  { speaker: "Sarah Chen", time: "1:10", text: "The main risk is around the legacy API integrations. We'll need your team's help mapping those to the new REST endpoints." },
  { speaker: currentUser.name, time: "1:30", text: "Absolutely, I'll get our integration team looped in. Let's set up a working session for that." },
  { speaker: "Sarah Chen", time: "1:45", text: "Perfect. I'll send over the technical spec document after this call so they can review it ahead of time." },
  { speaker: currentUser.name, time: "2:00", text: "Sounds good. Anything else on your end before we wrap up?" },
  { speaker: "Sarah Chen", time: "2:10", text: "Just one thing — we'll need access to the staging environment by Friday to start the POC." },
  { speaker: currentUser.name, time: "2:20", text: "I'll get that set up today. Thanks Sarah, great progress on the SOW." },
];

// Convert markdown-style notes text to BlockNote blocks
function markdownToBlocks(md: string): PartialBlock[] {
  if (!md.trim()) return [{ type: "paragraph" }];
  const blocks: PartialBlock[] = [];
  md.split("\n").forEach(line => {
    if (line.startsWith("## ")) {
      blocks.push({ type: "heading", props: { level: 2 }, content: [{ type: "text" as const, text: line.replace("## ", ""), styles: {} }] });
    } else if (line.startsWith("### ")) {
      blocks.push({ type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: line.replace("### ", ""), styles: {} }] });
    } else if (line.startsWith("- **")) {
      const m = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)/);
      if (m) {
        blocks.push({
          type: "bulletListItem",
          content: [
            { type: "text" as const, text: m[1], styles: { bold: true } },
            ...(m[2] ? [{ type: "text" as const, text: ": " + m[2], styles: {} }] : []),
          ],
        });
      } else {
        blocks.push({ type: "bulletListItem", content: [{ type: "text" as const, text: line.replace("- ", ""), styles: {} }] });
      }
    } else if (line.startsWith("- ")) {
      blocks.push({ type: "bulletListItem", content: [{ type: "text" as const, text: line.replace("- ", ""), styles: {} }] });
    } else if (line.trim() === "") {
      blocks.push({ type: "paragraph" });
    } else {
      blocks.push({ type: "paragraph", content: [{ type: "text" as const, text: line, styles: {} }] });
    }
  });
  return blocks.length > 0 ? blocks : [{ type: "paragraph" }];
}

// Convert plain text email to BlockNote blocks
function emailToBlocks(email: string): PartialBlock[] {
  if (!email.trim()) return [{ type: "paragraph" }];
  return email.split("\n").map(line => {
    if (line.trim() === "") return { type: "paragraph" as const };
    return { type: "paragraph" as const, content: [{ type: "text" as const, text: line, styles: {} }] };
  });
}

const summaryPoints = [
  "Migration timeline estimated at 3 weeks total (2 weeks migration + 1 week UAT)",
  "Legacy API integrations identified as the main risk area",
  "Integration team to be looped in for endpoint mapping",
  "Technical spec document to be shared post-meeting",
  "Staging environment access needed by Friday for POC",
];

// Methodology-specific dummy summary data
const methodologySummaries: Record<string, SummarySection[]> = {
  bant: [
    {
      title: "Budget", bullets: [
        "Sarah confirmed an approved budget of $150K for the migration project",
        "Additional $25K contingency for unforeseen integration issues",
        "Budget approval came from CFO last quarter — no further sign-off needed",
      ]
    },
    {
      title: "Authority", bullets: [
        "Sarah Chen (VP Engineering) is the primary decision-maker",
        "Final contract sign-off requires CTO approval — Sarah will handle internally",
        "Procurement team involvement only needed for contracts above $200K",
      ]
    },
    {
      title: "Need", bullets: [
        "Legacy system causing 3+ hours of downtime per month",
        "Current API integrations are brittle and blocking new feature development",
        "Team productivity impacted by manual data migration workarounds",
      ]
    },
    {
      title: "Timeline", bullets: [
        "Target go-live: end of Q2 (hard deadline tied to board reporting)",
        "POC must be completed by Friday to stay on track",
        "UAT window is fixed at 1 week — no room for extension",
      ]
    },
  ],
  meddic: [
    {
      title: "Metrics", bullets: [
        "Current system downtime: 3+ hours/month costing ~$45K in lost productivity",
        "Target: 99.9% uptime post-migration, saving 36+ engineering hours/month",
        "Expected ROI of 280% within first year based on internal analysis",
      ]
    },
    {
      title: "Economic Buyer", bullets: [
        "CTO James Park controls the budget — Sarah has direct line",
        "CFO pre-approved the $150K allocation in Q4 planning",
        "No board approval needed below $200K threshold",
      ]
    },
    {
      title: "Decision Criteria", bullets: [
        "Must support existing REST API patterns with backward compatibility",
        "Migration must complete within 3-week window to avoid Q2 reporting disruption",
        "Solution must pass SOC 2 compliance requirements",
      ]
    },
    {
      title: "Decision Process", bullets: [
        "Technical evaluation (current phase) → POC validation → Contract review → Sign-off",
        "Sarah runs technical evaluation; CTO gives final go/no-go",
        "Procurement involved only for contract paperwork (1-2 days)",
      ]
    },
    {
      title: "Identify Pain", bullets: [
        "Legacy API integrations failing regularly, causing customer-facing issues",
        "Engineering team spending 30% of time on maintenance vs. new features",
        "Manual data migration workarounds introduce data integrity risks",
      ]
    },
    {
      title: "Champion", bullets: [
        "Sarah Chen is the clear internal champion — initiated and is driving the project",
        "She has CTO's trust and authority to make technical recommendations",
        "Already socialized the migration plan with her engineering leads",
      ]
    },
  ],
  meddpicc: [
    {
      title: "Metrics", bullets: [
        "Current system downtime: 3+ hours/month costing ~$45K in lost productivity",
        "Target: 99.9% uptime post-migration, saving 36+ engineering hours/month",
        "Expected ROI of 280% within first year based on internal analysis",
      ]
    },
    {
      title: "Economic Buyer", bullets: [
        "CTO James Park controls the budget — Sarah has direct line",
        "CFO pre-approved the $150K allocation in Q4 planning",
      ]
    },
    {
      title: "Decision Criteria", bullets: [
        "Must support existing REST API patterns with backward compatibility",
        "Migration must complete within 3-week window to avoid Q2 reporting disruption",
        "Solution must pass SOC 2 compliance requirements",
      ]
    },
    {
      title: "Decision Process", bullets: [
        "Technical evaluation → POC validation → Contract review → Sign-off",
        "Sarah runs technical evaluation; CTO gives final go/no-go",
      ]
    },
    {
      title: "Paper Process", bullets: [
        "Standard vendor agreement — legal review takes 3-5 business days",
        "NDA already signed; MSA template being used from prior engagement",
        "Procurement turnaround is fast — Sarah confirmed 1-2 days max",
      ]
    },
    {
      title: "Identify Pain", bullets: [
        "Legacy API integrations failing regularly, causing customer-facing issues",
        "Engineering team spending 30% of time on maintenance vs. new features",
      ]
    },
    {
      title: "Champion", bullets: [
        "Sarah Chen is the clear internal champion — initiated and is driving the project",
        "She has CTO's trust and authority to make technical recommendations",
      ]
    },
    {
      title: "Competition", bullets: [
        "Evaluated AWS Migration Hub — too complex for their team size",
        "Considered building in-house but timeline doesn't allow it",
        "No other active vendor conversations — we're the frontrunner",
      ]
    },
  ],
  spiced: [
    {
      title: "Situation", bullets: [
        "Acme Corp running a legacy monolith with brittle API integrations",
        "250-person company scaling rapidly — infrastructure can't keep up",
        "Engineering team of 40 spending disproportionate time on maintenance",
      ]
    },
    {
      title: "Pain", bullets: [
        "3+ hours of system downtime per month impacting customer SLAs",
        "Legacy APIs breaking with every new feature deployment",
        "Manual data migration workarounds creating data integrity risks",
      ]
    },
    {
      title: "Impact", bullets: [
        "$45K/month in lost productivity from downtime and manual processes",
        "Customer churn risk — two enterprise clients flagged reliability concerns",
        "Engineering velocity reduced by 30% due to maintenance burden",
      ]
    },
    {
      title: "Critical Event", bullets: [
        "Board reporting deadline at end of Q2 — system must be stable",
        "Enterprise client renewal in 6 weeks — need reliability improvements before then",
        "New product launch blocked until migration completes",
      ]
    },
    {
      title: "Decision", bullets: [
        "Sarah Chen (VP Eng) owns the technical decision — strong champion",
        "CTO James Park gives final approval — aligned with Sarah's recommendation",
        "Budget pre-approved; procurement is a formality at this deal size",
      ]
    },
  ],
};

const combinedNotes = `## Key Discussion Points

### Migration Timeline
- Data migration phase: ~2 weeks
- Testing & UAT phase: ~1 week
- Total estimated timeline: 3 weeks
- Sarah seems confident about the timeline — good sign

### Risk Areas
- **Legacy API Integrations**: The existing API endpoints need to be mapped to the new REST system. This is the primary risk factor.
- ${currentUser.name}'s integration team will assist with endpoint mapping
- The legacy API concern is real — we hit similar issues with Datadog
- Should probably loop in Alex Kim too since he dealt with this before

### Action Items Discussed
- Working session to be scheduled for API mapping
- Technical spec document to be shared after the call
- Staging environment access to be provisioned by Friday
- Need to check with ${workspaceMembers[2].name.split(" ")[0]} about integration team availability
- Friday deadline for staging is tight, talk to DevOps today

### Next Steps
- Sarah to send technical spec document
- ${currentUser.name} to provision staging environment access
- Follow-up working session scheduled for next week`;

/**
 * Bring-your-own recorders whose imported summary is stored as a note with this
 * `source`. `labelKey` is an i18n key (resolved at render time).
 */
const EXTERNAL_NOTE_SOURCES = [
  { source: "granola", logo: "/logos/granola.svg", labelKey: "meetings.sourceGranola" },
  { source: "fireflies", logo: "/logos/fireflies.svg", labelKey: "meetings.sourceFireflies" },
] as const;
type AiNotesSource = "coffee" | typeof EXTERNAL_NOTE_SOURCES[number]["source"];

const granolaPreviewNotes = `## Meeting Overview

- Kickoff call to align on migration approach and timeline
- Sarah Chen leading technical evaluation on the customer side
- Strong buying signals throughout — team ready to move quickly

## Technical Requirements

- REST API migration from legacy SOAP endpoints
- SSO integration required before production cutover
- Staging environment needed by end of week for parallel testing

## Concerns Raised

- Legacy API endpoint mapping is the biggest unknown
- Team wants weekly touchpoints during migration phase
- Compliance review still pending on data residency

## Follow-ups

- Share technical spec document
- Schedule API mapping working session
- Loop in DevOps on staging provisioning`;

const followUpEmail = `Hi Sarah,

Thanks for the great discussion today on the migration timeline. Here's a quick recap of what we covered:

- The migration is estimated at 3 weeks (2 weeks migration + 1 week UAT)
- I'm looping in our integration team to help with the legacy API endpoint mapping
- I'll have staging environment access set up for you by Friday

Looking forward to receiving the technical spec document. Let's plan to schedule the working session for early next week.

Best,
${currentUser.name}`;

const suggestedTasks = [
  { id: "t1", title: "Set up staging environment access for Sarah Chen", priority: "High" as const, assignee: currentUser.name },
  { id: "t2", title: "Schedule API mapping working session with integration team", priority: "High" as const, assignee: currentUser.name },
  { id: "t3", title: "Review technical spec document from Sarah", priority: "Medium" as const, assignee: currentUser.name },
  { id: "t4", title: "Prepare legacy API endpoint inventory", priority: "Medium" as const, assignee: currentUser.name },
  { id: "t5", title: "Send calendar invite for follow-up check-in next week", priority: "Low" as const, assignee: currentUser.name },
];

const priorityOptions = ["High", "Medium", "Low"] as const;

/**
 * Preview-only address book for the dummy attendees. Fixture data in the same
 * class as `src/data/dummy` — never translated.
 */
const PREVIEW_ATTENDEE_EMAILS: Record<string, string> = {
  "Sarah Chen": "sarah@stripe.com",
  "James Wilson": "james@shopify.com",
  "Maria Garcia": "maria@hubspot.com",
  "Alex Kim": "alex@datadoghq.com",
  "Priya Patel": "priya@notion.so",
  "Tom Baker": "tom@figma.com",
  "Nina Rossi": "nina@canva.com",
  "David Okafor": "david@plaid.com",
};

const viewers = [
  { name: workspaceMembers[1].name, viewedAt: "Feb 28, 2026" },
];

/* ── Speaker color palette ────────────────── */
const speakerColors = [
  { bg: "bg-sky-500", bgLight: "bg-sky-200 text-sky-800", ring: "ring-sky-500" },
  { bg: "bg-amber-500", bgLight: "bg-amber-200 text-amber-800", ring: "ring-amber-500" },
  { bg: "bg-emerald-500", bgLight: "bg-emerald-200 text-emerald-800", ring: "ring-emerald-500" },
  { bg: "bg-violet-500", bgLight: "bg-violet-200 text-violet-800", ring: "ring-violet-500" },
  { bg: "bg-rose-500", bgLight: "bg-rose-200 text-rose-800", ring: "ring-rose-500" },
  { bg: "bg-cyan-500", bgLight: "bg-cyan-200 text-cyan-800", ring: "ring-cyan-500" },
];

/* ── Component ────────────────────────────── */

export default function MeetingIntelligenceSheet({ open, onOpenChange, note, defaultTab = "summary", hideOverlay = false, onBack }: MeetingIntelligenceSheetProps) {
  const workspaceId = useWorkspaceId();
  const { currentUser: realCurrentUser } = useCurrentUser();

  // Mirror the workspace-scoped sticky state SettingsPage uses for the
  // admin's chosen summary template + the set of custom templates. This lets
  // preview mode honor the admin's selection and only surface a dropdown
  // when there is actually more than one template the end user can pick.
  const _wsKeySuffix = workspaceId ? `:ws:${workspaceId}` : "";
  const [previewSummaryTemplate] = useStickyState<string>(
    `settings:summary-template${_wsKeySuffix}`,
    "general"
  );
  // Dummy "Customer Success" templates are preview-only — they let preview users
  // see a non-builtin entry in the summary dropdown before opening Settings.
  // Outside preview we seed nothing so this demo data never lands in production
  // localStorage (previewSavedTemplates is only read in preview branches anyway).
  const PREVIEW_DEFAULT_TEMPLATES = [
    { id: "customer-success-briefing", name: "Customer Success", type: "briefing" as const },
    { id: "customer-success-summary", name: "Customer Success", type: "summary" as const },
  ];
  const [previewSavedTemplates] = useStickyState<Array<{ id: string; name: string; type: "briefing" | "summary"; builtIn?: boolean }>>(
    "settings:custom-templates",
    isLovablePreview() ? PREVIEW_DEFAULT_TEMPLATES : []
  );

  // Backend data hooks
  const [getEngagement, { data: engagementData, loading: engagementLoading, error: engagementError }] = useGetEngagementLazyQuery({
    fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache for stale meetings
  });

  const [getEngagementNotes, { data: notesData }] = useGetNotesForEngagementLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Fetch engagement details and notes when sheet opens
  useEffect(() => {
    if (open && note?.id && workspaceId && !isLovablePreview()) {
      getEngagement({
        variables: {
          id: note.id,
          workspaceId: String(workspaceId),
        },
      });

      // Fetch notes for this engagement
      getEngagementNotes({
        variables: {
          filter: {
            workspaceId: String(workspaceId),
            engagementId: note.id,
          },
          first: 10,
        },
      });
    }
  }, [open, note?.id, workspaceId, getEngagement, getEngagementNotes]);

  // Use backend data when available, fallback to dummy data in preview mode
  // IMPORTANT: Verify the cached engagement ID matches the current note ID to prevent showing stale data
  const engagement = engagementData?.engagement;
  const isCorrectEngagement = engagement?.id === note?.id;
  const useBackendData = !isLovablePreview() && engagement && isCorrectEngagement;
  const videoSrc = useBackendData
    ? (engagement?.coffeeVideoUrl || engagement?.recallVideoUrl || engagement?.gongVideoUrl || engagement?.firefliesVideoUrl || null)
    : null;

  // Determine whether this meeting was actually recorded. In preview mode we
  // look up the dummy meeting by id; when backed by real data we defer to the
  // engagement's recording/transcript signals. When we can't determine, we
  // default to recorded so existing meetings keep their full UI.
  const isRecorded = (() => {
    if (useBackendData) {
      return !!(
        engagement?.recordingEnabled ||
        engagement?.hasTranscript ||
        engagement?.recallVideoUrl ||
        engagement?.coffeeVideoUrl ||
        engagement?.gongVideoUrl ||
        engagement?.granolaVideoUrl ||
        engagement?.firefliesVideoUrl
      );
    }
    const dummy = allMeetings.find((m: any) => m.id === note?.id);
    if (dummy) return dummy.recordingEnabled !== false;
    return true;
  })();

  // Feature flags
  const showViewedBy = useFeatureFlag(FEATURE_FLAGS.MEETING_INTELLIGENCE_VIEWED_BY);
  const showCompanyEdit = useFeatureFlag(FEATURE_FLAGS.MEETING_COMPANY_EDIT);

  // Get AI notes and manual notes from backend
  const engagementNotes = notesData?.notes?.edges.map(edge => edge?.node) || [];
  // AI notes can come from Coffee's own PostCallSummary and from a summary
  // imported from a bring-your-own recorder (note.source "granola" or
  // "fireflies"). Show a toggle only when more than one source has a note;
  // otherwise just render the one there is.
  const coffeeAiNote = engagementNotes.find(
    n => n?.aiGenerated === true && !EXTERNAL_NOTE_SOURCES.some(s => s.source === n?.source),
  );
  const externalNotes = EXTERNAL_NOTE_SOURCES
    .map(s => ({ ...s, note: engagementNotes.find(n => n?.source === s.source) }))
    .filter(s => s.note);
  const [aiNotesSource, setAiNotesSource] = useState<AiNotesSource>("coffee");
  // Which source is actually shown: honor the explicit choice when that note
  // exists, else fall back to whichever note is available.
  const effectiveSource: AiNotesSource =
    isLovablePreview()
      ? aiNotesSource
      : externalNotes.some(s => s.source === aiNotesSource)
        ? aiNotesSource
        : (coffeeAiNote ? "coffee" : (externalNotes[0]?.source ?? "coffee"));
  const aiNote = effectiveSource === "coffee"
    ? coffeeAiNote
    : externalNotes.find(s => s.source === effectiveSource)?.note;
  // Toggle options. Lovable preview simulates a Granola note on every recorded
  // meeting so the toggle is discoverable.
  const notesSourceOptions = isLovablePreview()
    ? EXTERNAL_NOTE_SOURCES.filter(s => s.source === "granola")
    : externalNotes;
  const showNotesSourceToggle = isLovablePreview()
    ? Boolean(combinedNotes.trim())
    : externalNotes.length + (coffeeAiNote ? 1 : 0) > 1;
  const manualNote = engagementNotes.find(n => n?.aiGenerated === false);

  // Parse AI notes content to BlockNote blocks
  const actualAiNotesBlocks = useMemo((): PartialBlock[] | null => {
    if (isLovablePreview()) {
      return null; // Use markdown rendering for preview mode
    }
    if (aiNote?.aiNotesContent) {
      try {
        // aiNotesContent is stored as JSON string, parse it
        const parsed = typeof aiNote.aiNotesContent === 'string'
          ? JSON.parse(aiNote.aiNotesContent)
          : aiNote.aiNotesContent;

        // Check if it's the AI notes structure with title and bullets
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Empty array means no AI notes generated yet
          if (parsed.length === 0 || (parsed[0] && typeof parsed[0] === 'object' && parsed[0].title)) {
            return parsed.length > 0 ? aiNotesToBlocks(parsed as AiNotesSection[]) : null;
          }
        }
      } catch (e) {
        console.error('Failed to parse AI notes:', e);
      }
    }
    return null; // No AI notes available
  }, [aiNote?.aiNotesContent]);

  // Parse manual notes blocks (BlockNote JSON) for editing
  const manualNoteBlocks = useMemo((): PartialBlock[] | null => {
    if (manualNote?.blocks) {
      try {
        const parsed = typeof manualNote.blocks === 'string'
          ? JSON.parse(manualNote.blocks)
          : manualNote.blocks;

        if (Array.isArray(parsed)) {
          return parsed as PartialBlock[];
        }
      } catch (e) {
        console.error('Failed to parse manual note blocks:', e);
      }
    }
    return null;
  }, [manualNote?.blocks]);

  // Parse manual notes blocks as markdown string for readonly display (AI Notes tab)
  const actualManualNotes = useMemo(() => {
    if (isLovablePreview()) {
      // Dummy manual notes fallback for preview mode
      return `## My Notes\n\n- Sarah seemed very confident about the timeline\n- The legacy API concern is real — we hit similar issues with Datadog\n- Should probably loop in Alex Kim too since he dealt with this before\n- Need to check with ${workspaceMembers[2].name.split(" ")[0]} about integration team availability\n- Friday deadline for staging is tight, talk to DevOps today`;
    }
    if (manualNoteBlocks) {
      // Convert blocks to markdown
      return manualNoteBlocks.map(block => {
        if (block.type === 'heading') {
          const level = block.props?.level || 2;
          const text = block.content?.map((c) => (c as { text?: string }).text || '').join('') || '';
          return '#'.repeat(level) + ' ' + text;
        }
        if (block.type === 'bulletListItem') {
          const text = block.content?.map((c) => (c as { text?: string }).text || '').join('') || '';
          return '- ' + text;
        }
        if (block.type === 'paragraph') {
          const text = block.content?.map((c) => (c as { text?: string }).text || '').join('') || '';
          return text;
        }
        return '';
      }).join('\n');
    }
    return ""; // Empty when no backend data
  }, [manualNoteBlocks]);

  // Mutations for note management
  const [updateNote] = useUpdateNoteMutation();
  const [createNote] = useCreateNoteMutation();
  const [shareEngagement] = useShareEngagementMutation();
  const [isSharing, setIsSharing] = useState(false);
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  // Group-based per-record access (distinct from the public share link).
  const { enabled: recordSharingEnabled } = useRecordSharingEnabled();
  const [showManageAccess, setShowManageAccess] = useState(false);
  // Share links are open to read-only users (own meetings, enforced by the
  // backend); clips unlock for them only with the `read-only-meetings` flag.
  // Per-record grants (Manage access) stay full-user only because the backend
  // requires data write for them.
  const canShareMeeting = useCanShareMeeting();
  const canUseMeetingCapture = useCanUseMeetingCapture();
  const canMutate = useCanMutate();

  // Auto-save manual notes with debouncing
  const [manualNoteContent, setManualNoteContent] = useState<PartialBlock[]>([{ type: "paragraph" }]);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Initialize manual note content when data loads
  useEffect(() => {
    if (manualNoteBlocks) {
      setManualNoteContent(manualNoteBlocks);
    } else if (isLovablePreview()) {
      // Set dummy blocks for preview mode ONLY
      setManualNoteContent([
        { type: "heading", props: { level: 2 }, content: [{ type: "text" as const, text: "My Notes", styles: {} }] },
        { type: "paragraph" },
        { type: "bulletListItem", content: [{ type: "text" as const, text: "Sarah seemed very confident about the timeline", styles: {} }] },
        { type: "bulletListItem", content: [{ type: "text" as const, text: "The legacy API concern is real — we hit similar issues with Datadog", styles: {} }] },
        { type: "bulletListItem", content: [{ type: "text" as const, text: "Should probably loop in Alex Kim too since he dealt with this before", styles: {} }] },
        { type: "bulletListItem", content: [{ type: "text" as const, text: `Need to check with ${workspaceMembers[2].name.split(" ")[0]} about integration team availability`, styles: {} }] },
        { type: "bulletListItem", content: [{ type: "text" as const, text: "Friday deadline for staging is tight, talk to DevOps today", styles: {} }] },
      ]);
    } else {
      // Empty editor for new manual notes
      setManualNoteContent([{ type: "paragraph" }]);
    }
  }, [manualNoteBlocks]);

  // Auto-save function
  const saveManualNote = useCallback(async (blocks: PartialBlock[]) => {
    if (isLovablePreview() || !workspaceId || !note?.id || !realCurrentUser) return;

    try {
      const blocksJson = JSON.stringify(blocks);

      if (manualNote?.id) {
        // Update existing note
        await updateNote({
          variables: {
            input: {
              id: manualNote.id,
              workspaceId: String(workspaceId),
              blocks: blocksJson,
            },
          },
        });
      } else {
        // Create new manual note
        await createNote({
          variables: {
            input: {
              workspaceId: String(workspaceId),
              userId: String(realCurrentUser.id),
              title: `Notes: ${note.name}`,
              blocks: blocksJson,
              engagementIds: [note.id],
            },
          },
        });
        // Refetch notes to get the new note
        getEngagementNotes({
          variables: {
            filter: {
              workspaceId: String(workspaceId),
              engagementId: note.id,
            },
            first: 10,
          },
        });
      }
    } catch (error) {
      console.error('Failed to save manual note:', error);
      toast.error(t("meetings.saveNotesFailed"));
    }
  }, [workspaceId, note?.id, note?.name, realCurrentUser, manualNote?.id, updateNote, createNote, getEngagementNotes, t]);

  // Handle manual note changes with debounced auto-save
  const handleManualNoteChange = useCallback((blocks: PartialBlock[]) => {
    setManualNoteContent(blocks);

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout for auto-save (1 second delay)
    const timeout = setTimeout(() => {
      saveManualNote(blocks);
    }, 1000);

    setSaveTimeout(timeout);
  }, [saveTimeout, saveManualNote]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  useEffect(() => { if (open) setActiveTab(defaultTab); }, [open, defaultTab]);
  const [notesMode, setNotesMode] = useState<"ai" | "manual">("ai");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideoOverlay, setShowVideoOverlay] = useState(true);
  // When set, playback is bounded to a clip's [start, end] and auto-pauses at end.
  const [activeClip, setActiveClip] = useState<{ start: number; end: number } | null>(null);
  const [barHoverX, setBarHoverX] = useState<Record<string, number | null>>({});
  const [aiQuestion, setAiQuestion] = useState("");
  const [qaMessages, setQaMessages] = useState<QAMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [askCoffeePanelOpen, setAskCoffeePanelOpen] = useState(false);
  const [askCoffeeQuery, setAskCoffeeQuery] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0.3);
  // Custom video controls: shared for both real <video> and preview simulation
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  // Clips: server-extracted segments of the recording (dual-mode via useClips).
  // Creating one kicks a backend extraction workflow; the row shows as
  // PENDING until the clip video is ready.
  type TranscriptClip = Clip;
  const { clips, createClip, renameClip, deleteClip } = useClips(note?.id);
  const [editingClipId, setEditingClipId] = useState<string | null>(null);
  const [confirmDeleteClipId, setConfirmDeleteClipId] = useState<string | null>(null);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  // Floating "Clip" action shown next to a transcript text selection.
  const [clipMenu, setClipMenu] = useState<{ top: number; left: number } | null>(null);
  const activeSegmentRef = useRef<HTMLDivElement>(null);
  const [selectedTasks, setSelectedTasks] = useState<Record<string, boolean>>({});
  const [addedTaskIds, setAddedTaskIds] = useState<Set<string>>(new Set());
  const [strikingTaskIds, setStrikingTaskIds] = useState<Set<string>>(new Set());
  const [isAddingTasks, setIsAddingTasks] = useState(false);
  const defaultDueDate = useMemo(() => addDays(new Date(), 3), []);
  const { addTasks, upsertTask, tasks: allContextTasks } = useTasks();
  const { createTask } = useTaskMutations();
  const [taskDetailId, setTaskDetailId] = useState<string | null>(null);
  const [pendingTaskTitle, setPendingTaskTitle] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<typeof people[0] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<typeof allCompanies[0] | null>(null);

  // Allow user to correct/change the meeting's associated company (frontend-only).
  const [companyOverride, setCompanyOverride] = useState<string | null>(null);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  useEffect(() => {
    // Reset override whenever a new note is opened
    setCompanyOverride(null);
    setEditingCompany(false);
    setCompanySearch("");
  }, [note?.id]);
  // Inline-editable note title
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  useEffect(() => {
    setTitleOverride(null);
    setEditingTitle(false);
    setTitleDraft("");
  }, [note?.id]);
  // Reset playback whenever the sheet is (re)opened or a new meeting is loaded,
  // so we never resume from the previous recording's position (the sheet instance
  // is reused across meetings).
  useEffect(() => {
    setCurrentTime(0);
    setVideoDuration(0);
    setIsPlaying(false);
    setShowVideoOverlay(true);
    setActiveClip(null);
    setEditingClipId(null);
    setConfirmDeleteClipId(null);
    setClipMenu(null);
  }, [note?.id, open]);
  // Stop playback at the end of the active clip (clip preview from the list).
  useEffect(() => {
    const v = videoRef.current;
    if (!activeClip || !v) return;
    const onTimeUpdate = () => {
      if (v.currentTime >= activeClip.end) {
        v.pause();
        setActiveClip(null);
      }
    };
    v.addEventListener("timeupdate", onTimeUpdate);
    return () => v.removeEventListener("timeupdate", onTimeUpdate);
  }, [activeClip]);
  // Dismiss the floating Clip action when the selection collapses (click-away).
  useEffect(() => {
    const onSelectionChange = () => {
      const sel = typeof window !== "undefined" ? window.getSelection() : null;
      if (!sel || sel.isCollapsed || !sel.toString().trim()) setClipMenu(null);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);
  // Changing a <source src> alone does not reload a <video>; force it to pick up
  // the new recording (which re-fires onLoadedMetadata → poster-frame seek).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;
    v.load();
  }, [videoSrc]);
  const displayedTitle = titleOverride ?? (engagement?.displayName || note?.name || "");
  const commitTitle = () => {
    const trimmed = titleDraft.trim();
    setEditingTitle(false);
    if (!trimmed || trimmed === displayedTitle) return;
    setTitleOverride(trimmed);
    if (note) (note as any).name = trimmed;
    toast.success(t("meetings.noteRenamed"));
  };
  const displayedCompany = companyOverride ?? note?.company ?? "";
  // The meeting's own start, once loaded. Openers pass a pre-formatted date string, but a deep
  // link (notification, email, Slack) knows only the meeting id and passes "", which left the
  // header reading "· · attendees". The fetched start also carries the time, which no opener did.
  // Until then, fall back to the opener's date — but many openers pass the raw `startTime` ISO
  // string, which flashed in the header while the meeting loaded. Format those the same way.
  const openerDate = note?.date ?? "";
  const headerDate = useBackendData && engagement?.startTime
    ? formatDateTime(engagement.startTime)
    : /^\d{4}-\d{2}-\d{2}T/.test(openerDate) ? formatDateTime(openerDate) : openerDate;
  const headerHasCompany = showCompanyEdit ? Boolean(displayedCompany) : Boolean(note?.company);
  const companyMatches = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    const list = allCompanies.filter(c =>
      c.name.toLowerCase() !== displayedCompany.toLowerCase() &&
      (!q || c.name.toLowerCase().includes(q))
    );
    return list.slice(0, 8);
  }, [companySearch, displayedCompany]);

  // Resolve pending task title to ID after addTasks re-renders
  useEffect(() => {
    if (pendingTaskTitle) {
      const found = allContextTasks.find(ct => ct.title === pendingTaskTitle);
      if (found) {
        setTaskDetailId(found.id);
        setPendingTaskTitle(null);
      }
    }
  }, [allContextTasks, pendingTaskTitle]);

  // Fetch workspace summary templates from backend
  const { data: templatesData } = useGetWorkspaceSummaryTemplatesQuery({
    variables: { workspaceId: workspaceId! },
    skip: !workspaceId || isLovablePreview(),
  });

  // Fetch workspace-level default summary template (set in Intelligence Settings →
  // Meetings). Falls back silently if the workspace_intelligence feature is not
  // enabled — we still have the per-user + isDefault fallbacks below.
  const { data: workspaceIntelData } = useGetWorkspaceIntelligenceQuery({
    variables: { workspaceId: workspaceId! },
    skip: !workspaceId || isLovablePreview(),
    errorPolicy: "ignore",
  });

  // Mutation to persist the end user's chosen summary template as their default.
  const [updateUserMutation] = useUpdateUserMutation();

  // Initialize chat agent for Ask AI feature (only when not in preview mode)
  // IMPORTANT: useMemo to prevent recreating context object on every render
  const chatContext: ChatContext | undefined = useMemo(() => {
    if (isLovablePreview() || !engagement?.id) {
      return undefined;
    }
    return {
      type: 'meeting',
      id: engagement.id,
    };
  }, [engagement?.id]);

  const { state: chatState, actions: chatActions } = useChatAgent({
    initialContext: chatContext,
    initializeSession: open && !!chatContext, // Only create session when sheet is actually open
  });

  // Reset chat when sheet closes
  useEffect(() => {
    if (!open) {
      chatActions.reset();
    }
  }, [open, chatActions]);

  // Resolve the default summary template id with this precedence:
  //   1. workspace-level default (Intelligence Settings → Meetings → Meeting Summary)
  //   2. per-user default (User.defaultSummaryTemplateId)
  //   3. any system template flagged isDefault
  //   4. first available template
  //   5. literal "default" sentinel for preview mode
  const isPreviewMode = isLovablePreview();
  const workspaceDefaultSummaryTemplateId = workspaceIntelData?.workspaceIntelligence?.defaultSummaryTemplateId ?? null;
  const defaultTemplateId = useMemo(() => {
    if (isPreviewMode) {
      // Mirror admin pick resolution used by the dropdown below so the
      // SelectValue isn't blank on first open. Falls back to "general-summary"
      // when the admin hasn't explicitly chosen a methodology.
      const summaryOnly = (previewSavedTemplates || []).filter((t) => t.type === "summary");
      if (previewSummaryTemplate && previewSummaryTemplate !== "general") {
        const exists = summaryOnly.some((t) => t.id === previewSummaryTemplate);
        if (exists) return previewSummaryTemplate;
      }
      return summaryOnly.find((t) => t.id === "general-summary")?.id
        ?? summaryOnly[0]?.id
        ?? "general-summary";
    }
    const templates = templatesData?.summaryTemplates?.edges.map((edge) => edge?.node) ?? [];
    if (workspaceDefaultSummaryTemplateId) {
      const exists = templates.some((t) => t?.id === workspaceDefaultSummaryTemplateId);
      if (exists) return workspaceDefaultSummaryTemplateId;
    }
    if (realCurrentUser?.defaultSummaryTemplateId) {
      const exists = templates.some((t) => t?.id === realCurrentUser.defaultSummaryTemplateId);
      if (exists) return realCurrentUser.defaultSummaryTemplateId;
    }
    const defaultTemplate = templates.find((node) => node?.isDefault);
    return defaultTemplate?.id || templates[0]?.id || "default";
  }, [workspaceDefaultSummaryTemplateId, realCurrentUser?.defaultSummaryTemplateId, templatesData, isPreviewMode, previewSummaryTemplate, previewSavedTemplates]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(defaultTemplateId);

  // Update selected template when default changes
  useEffect(() => {
    setSelectedTemplateId(defaultTemplateId);
  }, [defaultTemplateId]);

  const teamMembers = [
    ...workspaceMembers.map(m => ({ name: m.name })),
    ...people.map(p => ({ name: p.name })),
  ];
  const uniqueMembers = Array.from(new Map(teamMembers.map(m => [m.name, m])).values());

  // Use backend-stored email signature, or fallback to name / dummy
  const emailSignature = useMemo(() => {
    if (!isLovablePreview() && realCurrentUser) {
      if (realCurrentUser.emailSignature) {
        return realCurrentUser.emailSignature;
      }
      const name = realCurrentUser.preferredName || realCurrentUser.givenName || 'User';
      return name;
    }
    // Dummy signature for Lovable Preview
    return "Doug\nAccount Executive · Gazillion\ndoug@gazillion.com · +1 (555) 123-4567";
  }, [realCurrentUser]);

  // Parse backend data or use dummy data ONLY in preview mode
  const actualTranscript = useMemo(() => {
    if (isLovablePreview()) {
      return transcript; // Dummy data for preview mode
    }
    // Prefer Coffee's own Recall transcript (proper diarization + named
    // speakers); fall back to a bring-your-own-recorder transcript (Gong, then
    // Granola, then Fireflies) only when Recall isn't available.
    const sourceTranscript = engagement?.recallTranscript?.length
      ? engagement.recallTranscript
      : engagement?.gongTranscript?.length
        ? engagement.gongTranscript
        : engagement?.granolaTranscript?.length
          ? engagement.granolaTranscript
          : engagement?.firefliesTranscript?.length
            ? engagement.firefliesTranscript
            : null;
    if (sourceTranscript) {
      // Transform backend transcript to UI format
      return sourceTranscript.map((segment) => {
        const words = segment.words || [];
        const startTs = words.length > 0 ? words[0].startTimestamp : 0;
        const endTs = words.length > 0 ? words[words.length - 1].endTimestamp : 0;
        // Spoken duration of this segment (used for accurate talk-time %). Guard
        // against missing/backwards timestamps — fall back to 0 so callers can
        // fall back to text length.
        const duration = Math.max(0, (endTs ?? 0) - (startTs ?? 0));
        return {
          speaker: (segment.speaker && segment.speaker !== 'null') ? segment.speaker : 'Unknown',
          time: words.length > 0 ? formatTimestamp(startTs) : "0:00",
          timestamp: startTs,
          duration,
          text: words.map(w => w.text).join(' ') || '',
        };
      });
    }
    return []; // Empty when no backend data
  }, [engagement?.recallTranscript, engagement?.gongTranscript, engagement?.granolaTranscript, engagement?.firefliesTranscript]);

  // Parse a "m:ss" or "h:mm:ss" style time string into seconds.
  const parseTimeToSeconds = useCallback((s: string): number => {
    if (!s) return 0;
    const parts = s.split(":").map(Number);
    if (parts.some(Number.isNaN)) return 0;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] || 0;
  }, []);

  // Normalize each transcript segment to a numeric start time (seconds).
  const transcriptStarts = useMemo(
    () =>
      actualTranscript.map((t) =>
        typeof t.timestamp === "number" ? t.timestamp : parseTimeToSeconds(t.time),
      ),
    [actualTranscript, parseTimeToSeconds],
  );

  const activeSegmentIndex = useMemo(() => {
    if (!transcriptStarts.length) return -1;
    // Ignore the poster-frame seek (see displayTime): before the user starts a
    // real video, the transcript should not scroll/highlight to the seeked point.
    const effectiveTime = !isLovablePreview() && showVideoOverlay ? 0 : currentTime;
    let idx = -1;
    for (let i = 0; i < transcriptStarts.length; i++) {
      if (transcriptStarts[i] <= effectiveTime) idx = i;
      else break;
    }
    return idx;
  }, [transcriptStarts, currentTime, showVideoOverlay]);

  // Preview-mode playback simulation (no real <video>): tick currentTime
  useEffect(() => {
    if (!isLovablePreview() || !isPlaying) return;
    const dur = videoDuration || 2712; // ~45:12
    const id = setInterval(() => {
      setCurrentTime((t) => {
        const next = t + 0.5 * playbackRate;
        if (next >= dur) {
          setIsPlaying(false);
          return dur;
        }
        return next;
      });
    }, 500);
    return () => clearInterval(id);
  }, [isPlaying, playbackRate, videoDuration]);

  // Initialize preview duration once transcript is known
  useEffect(() => {
    if (isLovablePreview() && !videoDuration) {
      setVideoDuration(2712);
    }
  }, [videoDuration]);

  // Auto-scroll the transcript panel to keep the active segment in view
  useEffect(() => {
    if (activeSegmentIndex < 0) return;
    const el = activeSegmentRef.current;
    const container = transcriptScrollRef.current;
    if (!el || !container) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.top < cRect.top + 24 || eRect.bottom > cRect.bottom - 24) {
      // Scroll only within the transcript container so the page/sheet doesn't jump
      const delta = (eRect.top + eRect.height / 2) - (cRect.top + cRect.height / 2);
      container.scrollBy({ top: delta, behavior: "smooth" });
    }
  }, [activeSegmentIndex]);

  // Format seconds as m:ss or h:mm:ss
  const fmtTime = useCallback((s: number): string => {
    if (!Number.isFinite(s) || s < 0) s = 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
  }, []);

  // Unified control helpers (work for both real video and preview simulation)
  const seekTo = useCallback(
    (t: number) => {
      // A manual seek cancels any active clip bound (playClip re-sets it after
      // its own seek). This keeps scrubbing/skip from being cut off at clip end.
      setActiveClip(null);
      const dur = videoDuration || 0;
      const clamped = Math.max(0, dur ? Math.min(t, dur) : t);
      setCurrentTime(clamped);
      if (videoRef.current) videoRef.current.currentTime = clamped;
    },
    [videoDuration],
  );
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      if (v.paused) {
        // Starting from the initial poster overlay plays from the beginning
        // (the frame was seeked forward on load) — same behavior as the overlay.
        if (showVideoOverlay) v.currentTime = 0;
        setShowVideoOverlay(false);
        v.play();
      } else {
        v.pause();
      }
      return;
    }
    setIsPlaying((p) => !p);
  }, [showVideoOverlay]);
  const skip = useCallback(
    (delta: number) => seekTo(currentTime + delta),
    [currentTime, seekTo],
  );
  const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5];
  const setRate = useCallback((next: number) => {
    setPlaybackRate(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
    setSpeedMenuOpen(false);
  }, []);
  const toggleMute = useCallback(() => {
    setIsMuted((m) => {
      const next = !m;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  // Clip from current text selection inside the transcript
  const createClipFromSelection = useCallback(async () => {
    if (!canUseMeetingCapture) return;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    const text = sel?.toString().trim() ?? "";
    if (!sel || sel.rangeCount === 0 || !text) {
      toast.info(t("meetings.clipSelectFirst"));
      return;
    }
    const range = sel.getRangeAt(0);
    const container = transcriptScrollRef.current;
    if (!container || !container.contains(range.commonAncestorContainer)) {
      toast.info(t("meetings.clipSelectFirst"));
      return;
    }
    const findIdx = (node: Node | null): number | null => {
      let el: HTMLElement | null =
        node && node.nodeType === 1
          ? (node as HTMLElement)
          : node?.parentElement ?? null;
      while (el && el !== container) {
        const v = el.getAttribute?.("data-transcript-idx");
        if (v != null) return Number(v);
        el = el.parentElement;
      }
      return null;
    };
    const startIdx = findIdx(range.startContainer);
    const endIdx = findIdx(range.endContainer);
    if (startIdx == null || endIdx == null) {
      toast.info(t("meetings.clipSelectFirst"));
      return;
    }
    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    const startSec = transcriptStarts[lo] ?? 0;
    const endSec =
      transcriptStarts[hi + 1] ?? (videoDuration || startSec + 30);
    const defaultName =
      text.length > 60 ? `${text.slice(0, 60).trim()}…` : text;
    sel.removeAllRanges();
    try {
      await createClip({ startSec, endSec, name: defaultName });
      // In production the clip is created PENDING; the extraction workflow
      // fills in the video shortly (the row polls to READY).
      toast.success(isLovablePreview() ? t("meetings.clipAdded") : t("meetings.clipGenerating"));
    } catch (err) {
      console.error("Failed to create clip:", err);
      toast.error(t("meetings.clipCreateFailed"));
    }
  }, [transcriptStarts, videoDuration, createClip, canUseMeetingCapture, t]);
  // Show the floating Clip action anchored to the current transcript selection.
  // Positioned absolutely inside the (relative) transcript scroll container so
  // it isn't affected by the Sheet's transform or portal constraints.
  const showClipMenuFromSelection = useCallback(() => {
    if (!canUseMeetingCapture) return;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    const text = sel?.toString().trim() ?? "";
    const container = transcriptScrollRef.current;
    if (!sel || sel.rangeCount === 0 || !text || !container) {
      setClipMenu(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      setClipMenu(null);
      return;
    }
    const selRect = range.getBoundingClientRect();
    if (!selRect || (!selRect.width && !selRect.height)) {
      setClipMenu(null);
      return;
    }
    const cRect = container.getBoundingClientRect();
    const above = selRect.top - cRect.top + container.scrollTop - 38;
    const below = selRect.bottom - cRect.top + container.scrollTop + 6;
    // Prefer above the selection; flip below if that would clip at the top.
    const top = above < container.scrollTop + 2 ? below : above;
    const left = selRect.left - cRect.left + selRect.width / 2;
    setClipMenu({ top, left });
  }, [canUseMeetingCapture]);
  const playClip = useCallback(
    (clip: TranscriptClip) => {
      // Dismiss the poster overlay and play the source recording bounded to the
      // clip's window — activeClip drives the stop-at-end effect below.
      setShowVideoOverlay(false);
      seekTo(clip.startSec);
      const v = videoRef.current;
      if (v) {
        setActiveClip({ start: clip.startSec, end: clip.endSec });
        v.play();
      } else {
        setIsPlaying(true);
      }
    },
    [seekTo],
  );
  const copyClipLink = useCallback(
    async (clip: TranscriptClip) => {
      if (!canUseMeetingCapture) return;
      if (!clip.shareSlug) {
        toast.info(t("meetings.clipStillGenerating"));
        return;
      }
      const url = `${window.location.origin}/shared/clip/${clip.shareSlug}`;
      await navigator.clipboard.writeText(url);
      toast.success(t("meetings.clipLinkCopied"));
    },
    [canUseMeetingCapture, t],
  );

  const speakerAvatarMap = useMemo((): Record<string, string> => {
    if (!engagement?.contacts?.length) return {};
    const map: Record<string, string> = {};
    for (const c of engagement.contacts) {
      if (!c) continue;
      const name = [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(" ").trim() || c.primaryEmail || "";
      const url = c.properties?.profileImageUrl || c.profileImageUrl || "";
      if (name && url) map[name] = url;
    }
    return map;
  }, [engagement?.contacts]);

  const actualSummaryPoints = useMemo(() => {
    if (isLovablePreview()) {
      return summaryPoints; // Dummy data for preview mode
    }
    if (engagement?.aiSummary) {
      try {
        const parsed = JSON.parse(engagement.aiSummary);
        // Extract summary points from parsed JSON
        let items: unknown[] = [];
        if (Array.isArray(parsed)) {
          items = parsed;
        } else if (parsed.summary && Array.isArray(parsed.summary)) {
          items = parsed.summary;
        } else if (parsed.points && Array.isArray(parsed.points)) {
          items = parsed.points;
        }

        if (items.length === 0) return [];

        // If items are plain strings, return as-is
        if (typeof items[0] === 'string') return items as string[];

        // If items are section objects (SummarySection[]), flatten their bullets to strings
        const flat: string[] = [];
        (items as Array<{ title?: string; bullets?: Array<string | { text?: string }>; body?: string }>).forEach((section) => {
          if (Array.isArray(section.bullets)) {
            section.bullets.forEach((b) => {
              flat.push(typeof b === 'string' ? b : String(b?.text ?? ''));
            });
          } else if (section.body) {
            flat.push(String(section.body));
          }
        });
        return flat.length > 0 ? flat : [];
      } catch (e) {
        console.error('Failed to parse aiSummary:', e);
      }
    }
    return []; // Empty when no backend data
  }, [engagement?.aiSummary]);

  const actualFollowUpEmail = useMemo(() => {
    if (isLovablePreview()) {
      return followUpEmail; // Dummy data for preview mode
    }
    if (engagement?.aiFollowup) {
      // aiFollowup can be either a plain string or JSON
      if (typeof engagement.aiFollowup === 'string') {
        // Try to parse as JSON first
        try {
          const parsed = JSON.parse(engagement.aiFollowup);
          // Extract email from parsed JSON object
          if (parsed.email) {
            return parsed.email;
          } else if (parsed.followUpEmail) {
            return parsed.followUpEmail;
          }
          // If it's just a string in JSON, this won't happen but handle anyway
          return engagement.aiFollowup;
        } catch (e) {
          // Not JSON, just use the string directly
          return engagement.aiFollowup;
        }
      }
    }
    return ""; // Empty when no backend data
  }, [engagement?.aiFollowup]);

  // The email body editor is fully editable — capture edits so Copy and
  // Open Draft use what the user sees, not the original AI text. Edits are
  // tagged with the meeting they belong to, so switching meetings never
  // shows another meeting's edits (even on the first render after the
  // switch). A draft saved via Open Draft → Save Draft is persisted under the
  // meeting's draft key and seeds the tab when the sheet is (re)opened.
  // `note` is nullable and these hooks run before the `if (!note) return null`
  // guard below, so they must use optional chaining like every other hook here.
  const followUpDraftKey = meetingFollowUpDraftKey(note?.id);
  const [editedFollowUpEmail, setEditedFollowUpEmail] = useState<{ noteId: string; body: string } | null>(null);
  // Read storage only while the sheet is open (it can stay mounted closed).
  const savedFollowUpDraftBody = useMemo(
    () => (open && followUpDraftKey ? getEmailDraft(workspaceId, followUpDraftKey)?.body ?? null : null),
    [open, followUpDraftKey, workspaceId],
  );
  const effectiveFollowUpEmail =
    (editedFollowUpEmail && editedFollowUpEmail.noteId === note?.id ? editedFollowUpEmail.body : null)
    ?? savedFollowUpDraftBody
    ?? actualFollowUpEmail;
  // Bumped when Save Draft writes a new body, to remount the tab editor with it.
  const [followUpEditorRev, setFollowUpEditorRev] = useState(0);
  const handleFollowUpEditorChange = (text: string) => {
    if (!note?.id) return;
    setEditedFollowUpEmail({ noteId: note.id, body: text });
    // Keep an existing saved draft in step with in-tab edits, so an older
    // saved body never overrides them on the next Open Draft.
    updateEmailDraftBody(workspaceId, followUpDraftKey, text);
  };
  const handleFollowUpDraftSaved = ({ body }: { subject: string; body: string }) => {
    if (!note?.id) return;
    setEditedFollowUpEmail({ noteId: note.id, body });
    setFollowUpEditorRev(r => r + 1);
  };

  const actualSuggestedTasks = useMemo(() => {
    if (isLovablePreview()) {
      return suggestedTasks; // Dummy data for preview mode
    }
    if (engagement?.taskSuggestions && engagement.taskSuggestions.length > 0) {
      return engagement.taskSuggestions.map(task => {
        // Capitalize priority to match UI expectations (backend returns lowercase)
        const rawPriority = task.priority || "medium";
        const capitalizedPriority = rawPriority.charAt(0).toUpperCase() + rawPriority.slice(1).toLowerCase();

        return {
          id: task.id,
          title: task.title,
          priority: capitalizedPriority as "High" | "Medium" | "Low",
          assignee: currentUser.name,
        };
      });
    }
    return []; // Empty when no backend data
  }, [engagement?.taskSuggestions]);

  // Check if selected template is the "General" default template (shows bullet points)
  const selectedTemplate = useMemo(() => {
    if (isPreviewMode || selectedTemplateId === "default") return null;
    const templates = templatesData?.summaryTemplates?.edges.map((edge) => edge?.node) ?? [];
    return templates.find((t) => t?.id === selectedTemplateId) || null;
  }, [templatesData, selectedTemplateId, isPreviewMode]);

  // Parse summary from backend engagement.summaries based on selected template
  const actualSummary = useMemo(() => {
    if (useBackendData && engagement?.summaries && selectedTemplateId && selectedTemplateId !== "default") {
      // Find summary matching selected template ID
      const matchedSummary = engagement.summaries.find(
        (s) => s?.summaryTemplateId === selectedTemplateId
      );

      if (matchedSummary?.summary) {
        // summary is GenericScalar - it could be already parsed or a string
        const parsed = typeof matchedSummary.summary === "string"
          ? JSON.parse(matchedSummary.summary)
          : matchedSummary.summary;

        // Extract sections array from parsed data
        if (parsed?.sections && Array.isArray(parsed.sections)) {
          return parsed.sections as SummarySection[];
        } else if (Array.isArray(parsed)) {
          return parsed as SummarySection[];
        }
      }
    }
    return null;
  }, [useBackendData, engagement?.summaries, selectedTemplateId]);

  // Derive summary blocks directly from actualSummary instead of using state
  const summaryBlocks = useMemo(() => {
    if (actualSummary) {
      return summaryToBlocks(actualSummary);
    }
    // In preview mode, check if a methodology template is selected
    if (isPreviewMode && selectedTemplateId && methodologySummaries[selectedTemplateId]) {
      return summaryToBlocks(methodologySummaries[selectedTemplateId]);
    }
    return undefined;
  }, [actualSummary, isPreviewMode, selectedTemplateId]);

  // ── Editable Summary + AI Notes (flag: meeting-summary-edit) ──
  // Flag off → editors behave exactly as before (typing is never saved).
  // Flag on → debounced auto-save, same 1s cadence as Manual Notes. We never
  // refetch after a save: the engagement/notes lazy queries are network-only
  // and a refetch would remount the editors and wipe in-progress typing.
  // Instead the latest edited blocks are kept per target (note id, or
  // engagement + template) and reused as initialContent whenever an editor
  // remounts (tab/template/source switch), so a different meeting or template
  // never shows another target's edits.
  const summaryEditEnabled = useFeatureFlag(FEATURE_FLAGS.MEETING_SUMMARY_EDIT);
  const canEditSummary = summaryEditEnabled && canMutate && !isLovablePreview();
  const [updateEngagementSummary] = useUpdateEngagementSummaryMutation();
  const editedBlocksRef = useRef<Record<string, PartialBlock[]>>({});
  const { schedule: scheduleEditSave, flush: flushEditSaves } = useKeyedDebouncedSave(1000);

  // Persist pending edits as soon as the sheet closes (unmount flushes too).
  useEffect(() => {
    if (!open) flushEditSaves();
  }, [open, flushEditSaves]);

  const summaryTemplateEditKey = engagement?.id && selectedTemplateId ? `summary:${engagement.id}:${selectedTemplateId}` : null;
  const summaryLegacyEditKey = engagement?.id ? `summary:${engagement.id}:legacy` : null;
  const aiNotesEditKey = aiNote?.id ? `ai-notes:${aiNote.id}` : null;
  const getEditedBlocks = (key: string | null) =>
    canEditSummary && key ? editedBlocksRef.current[key] : undefined;
  const editedTemplateSummaryBlocks = getEditedBlocks(summaryTemplateEditKey);
  const editedLegacySummaryBlocks = getEditedBlocks(summaryLegacyEditKey);
  const editedAiNotesBlocks = getEditedBlocks(aiNotesEditKey);
  // Which summary editor is shown. Once the user has edited one, keep showing
  // it even if a save empties the cached summary (e.g. everything deleted),
  // so the editor never unmounts mid-typing.
  const showTemplateSummary = !!((summaryBlocks && summaryBlocks.length > 1) || editedTemplateSummaryBlocks);
  const showLegacySummary = !showTemplateSummary && (actualSummaryPoints.length > 0 || !!editedLegacySummaryBlocks);

  const saveEngagementSummary = useCallback(async (
    target: { engagementId: string; summaryTemplateId: string | null; originalSections: SummarySection[] | null },
    blocks: PartialBlock[],
  ) => {
    if (!canEditSummary || !workspaceId) return;
    const sections = target.summaryTemplateId
      ? blocksToSummarySections(blocks, target.originalSections)
      : blocksToLegacySummarySections(blocks);
    try {
      await updateEngagementSummary({
        variables: {
          input: {
            workspaceId: String(workspaceId),
            engagementId: target.engagementId,
            ...(target.summaryTemplateId ? { summaryTemplateId: target.summaryTemplateId } : {}),
            sections: JSON.stringify(sections),
          },
        },
      });
    } catch (error) {
      console.error("Failed to save meeting summary:", error);
      toast.error(t("meetings.saveSummaryFailed"));
    }
  }, [canEditSummary, workspaceId, updateEngagementSummary, t]);

  const saveAiNotes = useCallback(async (noteId: string, blocks: PartialBlock[]) => {
    if (!canEditSummary || !workspaceId) return;
    try {
      await updateNote({
        variables: {
          input: {
            id: noteId,
            workspaceId: String(workspaceId),
            aiNotesContent: JSON.stringify(blocksToAiNotesSections(blocks, t("meetings.aiNotes"))),
          },
        },
      });
    } catch (error) {
      console.error("Failed to save AI notes:", error);
      toast.error(t("meetings.saveNotesFailed"));
    }
  }, [canEditSummary, workspaceId, updateNote, t]);

  const handleTemplateSummaryChange = useCallback((blocks: PartialBlock[]) => {
    if (!canEditSummary || !summaryTemplateEditKey || !engagement?.id) return;
    editedBlocksRef.current[summaryTemplateEditKey] = blocks;
    const target = { engagementId: engagement.id, summaryTemplateId: selectedTemplateId, originalSections: actualSummary };
    scheduleEditSave(summaryTemplateEditKey, () => saveEngagementSummary(target, blocks));
  }, [canEditSummary, summaryTemplateEditKey, engagement?.id, selectedTemplateId, actualSummary, scheduleEditSave, saveEngagementSummary]);

  const handleLegacySummaryChange = useCallback((blocks: PartialBlock[]) => {
    if (!canEditSummary || !summaryLegacyEditKey || !engagement?.id) return;
    editedBlocksRef.current[summaryLegacyEditKey] = blocks;
    const target = { engagementId: engagement.id, summaryTemplateId: null, originalSections: null };
    scheduleEditSave(summaryLegacyEditKey, () => saveEngagementSummary(target, blocks));
  }, [canEditSummary, summaryLegacyEditKey, engagement?.id, scheduleEditSave, saveEngagementSummary]);

  const handleAiNotesChange = useCallback((blocks: PartialBlock[]) => {
    if (!canEditSummary || !aiNotesEditKey || !aiNote?.id) return;
    editedBlocksRef.current[aiNotesEditKey] = blocks;
    const noteId = aiNote.id;
    scheduleEditSave(aiNotesEditKey, () => saveAiNotes(noteId, blocks));
  }, [canEditSummary, aiNotesEditKey, aiNote?.id, scheduleEditSave, saveAiNotes]);

  // Helper function to format timestamp in seconds to "M:SS" format
  function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /* Speaker analytics */
  const speakers = [...new Set(actualTranscript.map(t => t.speaker))];
  const speakerColorMap: Record<string, typeof speakerColors[0]> = {};
  speakers.forEach((s, i) => { speakerColorMap[s] = speakerColors[i % speakerColors.length]; });

  const speakerSegments: Record<string, number[]> = {};
  speakers.forEach(s => { speakerSegments[s] = []; });
  actualTranscript.forEach((t, i) => { speakerSegments[t.speaker].push(i); });
  const totalSegments = actualTranscript.length;

  // Talk-time share per speaker. Prefer actual spoken duration; if the source has
  // no usable timing (all durations 0), fall back to text length, then to a plain
  // segment count. Counting segments alone reads ~evenly when speakers alternate
  // (e.g. one long monologue vs. many short replies), so it is the last resort.
  const speakerTalk: Record<string, number> = {};
  speakers.forEach(s => { speakerTalk[s] = 0; });
  const totalDuration = actualTranscript.reduce((sum, t) => sum + (("duration" in t ? t.duration : 0) || 0), 0);
  const totalTextLen = actualTranscript.reduce((sum, t) => sum + (t.text?.length || 0), 0);
  const talkMetric: "duration" | "text" | "segments" =
    totalDuration > 0 ? "duration" : totalTextLen > 0 ? "text" : "segments";
  actualTranscript.forEach(t => {
    const inc = talkMetric === "duration"
      ? (("duration" in t ? t.duration : 0) || 0)
      : talkMetric === "text"
        ? (t.text?.length || 0)
        : 1;
    speakerTalk[t.speaker] += inc;
  });
  const totalTalk =
    talkMetric === "duration" ? totalDuration
      : talkMetric === "text" ? totalTextLen
        : totalSegments;

  // On the Recording tab the Ask bar doubles as the transcript search
  // ("Search transcript or ask questions…"): typing filters live, Enter sends
  // the question to Ask Coffee (which clears the bar, and so the filter).
  const searchQuery = activeTab === "recording" ? aiQuestion.trim().toLowerCase() : "";
  const filteredTranscript = searchQuery
    ? actualTranscript.filter(t => t.text.toLowerCase().includes(searchQuery) || t.speaker.toLowerCase().includes(searchQuery))
    : actualTranscript;

  const handleCopyTranscript = () => {
    navigator.clipboard.writeText(actualTranscript.map(t => `[${t.time}] ${t.speaker}: ${t.text}`).join("\n"));
    toast.success(t("meetings.transcriptCopied"));
  };

  const handleSeekVideo = (timestamp: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
      videoRef.current.play();
      setShowVideoOverlay(false);
    }
  };

  // Mock Q&A responses based on meeting context
  const getMockMeetingAnswer = (question: string): string => {
    const q = question.toLowerCase();
    const meetingName = note?.name || "this meeting";
    const hasTranscript = actualTranscript.length > 0;
    const hasSummary = actualSummaryPoints.length > 0;
    const hasFollowup = actualFollowUpEmail.trim().length > 0;
    const hasTasks = actualSuggestedTasks.length > 0;

    if (/what.*discuss|topic|agenda/i.test(q)) {
      if (hasSummary) {
        return `Based on the meeting summary, key topics discussed include:\n\n${actualSummaryPoints.slice(0, 3).map(p => `• ${p}`).join('\n')}\n\nWould you like me to elaborate on any specific point?`;
      }
      return `This meeting (${meetingName}) covered several important topics. The full transcript and summary are available in the tabs above for detailed insights.`;
    }
    if (/action.*item|task|follow.?up|next.*step/i.test(q)) {
      if (hasTasks) {
        return `Here are the key action items from this meeting:\n\n${actualSuggestedTasks.slice(0, 3).map((t, i) => `${i + 1}. ${t.title} (${t.priority} priority)`).join('\n')}\n\nYou can review all tasks in the Tasks tab.`;
      }
      if (hasFollowup) {
        return `Based on the meeting discussion, a follow-up email has been drafted. Check the Email tab for the complete message and next steps.`;
      }
      return `Task suggestions are being generated for this meeting. Check back shortly or review the transcript for manual action items.`;
    }
    if (/who.*say|who.*mention|quote|transcript/i.test(q)) {
      if (hasTranscript) {
        return `The full transcript is available in the Transcript tab. You can click on any timestamp to jump to that point in the recording. What specific topic or person are you looking for?`;
      }
      return `The transcript for this meeting is being processed. It will be available shortly for searching specific quotes and speakers.`;
    }
    if (/summary|brief|overview/i.test(q)) {
      if (hasSummary) {
        return `Here's a quick overview:\n\n${actualSummaryPoints.slice(0, 4).map(p => `• ${p}`).join('\n')}\n\nFor more detail, check the Summary tab where you can also switch between different summary templates.`;
      }
      return `The AI summary is being generated for this meeting. You can review the raw transcript in the meantime.`;
    }
    if (/decision|agreement|conclusion/i.test(q)) {
      return `Key decisions and agreements from ${meetingName} can be found in:\n\n• Meeting Summary - for high-level outcomes\n• Suggested Tasks - for agreed action items\n• Follow-up Email - for next steps communicated to attendees`;
    }
    return `I found information about "${question}" in this meeting. You can explore:\n\n• **Transcript** - Full conversation with timestamps\n• **Summary** - AI-generated highlights\n• **Tasks** - Suggested follow-up actions\n• **Email** - Draft follow-up message\n\nWhat specific aspect would you like to know more about?`;
  };

  const handleAskAI = async () => {
    if (!aiQuestion.trim()) return;
    const question = aiQuestion.trim();
    setAiQuestion("");
    setAskCoffeeQuery(question);
    // Defer opening to next tick to ensure query state is updated first
    setTimeout(() => setAskCoffeePanelOpen(true), 0);
  };

  const selectedCount = Object.entries(selectedTasks).filter(([id, v]) => v && !addedTaskIds.has(id)).length;

  // Helper to create a single task in the backend
  const createSuggestedTask = useCallback(async (taskData: {
    title: string;
    priority: string;
    dueDate: Date;
    silent?: boolean;
  }) => {
    if (isLovablePreview()) {
      // In preview mode, use dummy context
      addTasks([{
        title: taskData.title,
        priority: taskData.priority,
        assignee: realCurrentUser?.preferredName || realCurrentUser?.givenName || currentUser.name,
        dueDate: format(taskData.dueDate, "MMM d, yyyy"),
      }]);
      return;
    }

    // In production, create real backend task
    const priorityEnum = taskData.priority === 'High' ? NotePriorityEnum.High
      : taskData.priority === 'Medium' ? NotePriorityEnum.Medium
        : NotePriorityEnum.Low;

    // Get associations from the engagement (meeting)
    const engagementId = engagement?.id;
    const contactIds = engagement?.contacts
      ?.map(c => c?.id).filter(Boolean) as string[] | undefined;
    const accountIds = engagement?.accounts
      ?.map(a => a?.id).filter(Boolean) as string[] | undefined;

    await createTask({
      title: taskData.title,
      priority: priorityEnum,
      dueDate: taskData.dueDate.toISOString(),
      blocks: JSON.stringify([{ type: "paragraph" }]), // Empty description
      engagementIds: engagementId ? [engagementId] : undefined,
      contactIds: contactIds?.length ? contactIds : undefined,
      accountIds: accountIds?.length ? accountIds : undefined,
      silent: taskData.silent,
    });
  }, [isLovablePreview, addTasks, createTask, realCurrentUser, engagement]);

  // Early return check must come after all hooks
  if (!note) return null;

  // The real <video> is seeked forward on load to show a non-black poster
  // frame (see onLoadedMetadata below). That seek must not leak into the
  // scrubber/time readout — the user should see 0:00 until they actually
  // start playback (the play overlay resets currentTime to 0). Preview mode
  // has no poster seek, so it always reflects the simulated currentTime.
  const displayTime = !isLovablePreview() && showVideoOverlay ? 0 : currentTime;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent expandable hideOverlay={hideOverlay} className="meeting-intelligence-sheet sm:max-w-[50vw] w-full overflow-y-auto p-0">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-background border-b border-border px-6 pt-6 pb-4">
            {onBack && (
              <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
                <ArrowLeft className="h-4 w-4" /> {t("meetings.back")}
              </button>
            )}
            <SheetHeader className="mb-0">
              {/* Prefer the fetched engagement's displayName once available so opening
                  this sheet from a brief link (where note.name is the link text like
                  "Last call (April 15)") still shows the real meeting title. */}
              {editingTitle ? (
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitTitle(); }
                    if (e.key === "Escape") { setEditingTitle(false); }
                  }}
                  className="text-lg font-display bg-transparent border-b border-accent outline-none w-full py-0 px-0"
                />
              ) : (
                <SheetTitle
                  className="text-lg font-display group/title inline-flex items-center gap-1.5 cursor-text hover:bg-muted/40 rounded px-1 -mx-1 transition"
                  onClick={() => { setTitleDraft(displayedTitle); setEditingTitle(true); }}
                  title={t("meetings.clickToRename")}
                >
                  <span>{displayedTitle}</span>
                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover/title:opacity-100 transition shrink-0" />
                </SheetTitle>
              )}
              <p className="text-xs text-muted-foreground">
                {headerDate}
                {headerDate && " · "}
                {showCompanyEdit ? (
                <span className="relative inline-flex items-center gap-1 group/company align-baseline">
                  {editingCompany ? (
                    <span className="relative">
                      <input
                        autoFocus
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") { setEditingCompany(false); setCompanySearch(""); }
                          if (e.key === "Enter" && companyMatches[0]) {
                            setCompanyOverride(companyMatches[0].name);
                            setEditingCompany(false);
                            setCompanySearch("");
                          }
                        }}
                        onBlur={() => setTimeout(() => setEditingCompany(false), 150)}
                        placeholder={displayedCompany || t("meetings.searchCompanies")}
                        className="bg-transparent border-b border-accent outline-none text-xs py-0 px-0 w-40"
                      />
                      {(companyMatches.length > 0) && (
                        <div className="absolute top-full left-0 mt-1 z-50 rounded-md border border-border bg-popover shadow-md py-1 max-h-48 overflow-y-auto min-w-[200px]">
                          {companyMatches.map(c => (
                            <button
                              key={c.id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setCompanyOverride(c.name);
                                setEditingCompany(false);
                                setCompanySearch("");
                              }}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition truncate"
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </span>
                  ) : (
                    <>
                      <button
                        className="hover:text-foreground hover:underline transition"
                        onClick={() => {
                          if (useBackendData && engagement?.accounts?.length) {
                            const account = engagement.accounts.find(a =>
                              (a.properties?.name || a.domain) === displayedCompany
                            ) || engagement.accounts[0];
                            setSelectedCompany({
                              id: account.id,
                              name: account.properties?.name || account.domain || 'Unknown',
                              domain: account.domain,
                              website: account.domain,
                              location: account.location,
                              linkedin: account.properties?.linkedinUrl,
                              phone: account.properties?.phone,
                              industry: account.properties?.industry,
                              employees: account.properties?.companySizeCategory,
                              revenue: account.properties?.revenueRange,
                              description: account.properties?.description,
                            });
                          } else {
                            const comp = allCompanies.find(c => c.name === displayedCompany);
                            if (comp) setSelectedCompany(comp);
                          }
                        }}
                      >
                        {displayedCompany}
                      </button>
                      <button
                        type="button"
                        aria-label={t("meetings.changeCompany")}
                        title={t("meetings.changeCompany")}
                        onClick={() => { setEditingCompany(true); setCompanySearch(""); }}
                        className="opacity-0 group-hover/company:opacity-100 transition text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </span>
                ) : (
                <button
                  className="hover:text-foreground hover:underline transition"
                  onClick={() => {
                    if (useBackendData && engagement?.accounts?.length) {
                      const account = engagement.accounts.find(a =>
                        (a.properties?.name || a.domain) === (note?.company ?? "")
                      ) || engagement.accounts[0];
                      setSelectedCompany({
                        id: account.id,
                        name: account.properties?.name || account.domain || 'Unknown',
                        domain: account.domain,
                        website: account.domain,
                        location: account.location,
                        linkedin: account.properties?.linkedinUrl,
                        phone: account.properties?.phone,
                        industry: account.properties?.industry,
                        employees: account.properties?.companySizeCategory,
                        revenue: account.properties?.revenueRange,
                        description: account.properties?.description,
                      });
                    } else {
                      const comp = allCompanies.find(c => c.name === note?.company);
                      if (comp) setSelectedCompany(comp);
                    }
                  }}
                >
                  {note.company}
                </button>
                )}
                {headerHasCompany && " · "}
                {(() => {
                  // Build attendee list from backend contacts (by ID, not name) with dummy fallback
                  const attendees: Array<{ key: string; displayName: string; person: typeof people[0] | null }> = [];
                  if (useBackendData && engagement?.contacts?.length) {
                    for (const contact of engagement.contacts) {
                      if (!contact) continue;
                      const accountName = (contact as any)?.accounts?.[0]?.properties?.name || '';
                      const displayName = `${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail || '';
                      attendees.push({
                        key: contact.id,
                        displayName,
                        person: {
                          id: contact.id,
                          name: displayName,
                          title: contact.properties?.jobTitle || '',
                          company: accountName,
                          email: contact.primaryEmail || '',
                          phone: contact.properties?.phone || '',
                          linkedin: contact.properties?.linkedinProfileUrl || '',
                          location: '',
                          lastEmail: '', lastMeeting: '', nextMeeting: '', owner: '', createdBy: '', avatar: '',
                        },
                      });
                    }
                  } else {
                    // Fallback to note.people names for dummy/preview mode
                    for (const name of note.people) {
                      const dummyPerson = people.find(p => p.name === name);
                      const m = !dummyPerson ? workspaceMembers.find(m => m.name === name) : null;
                      attendees.push({
                        key: name,
                        displayName: name,
                        person: dummyPerson || (m ? { id: `wm-${m.id}`, name: m.name, company: "", title: t("meetings.teamMember"), email: m.email, phone: "", linkedin: "", location: "", lastEmail: "", lastMeeting: "", nextMeeting: "", owner: "", createdBy: "", avatar: "" } : null),
                      });
                    }
                  }
                  return attendees.map((a, i) => (
                    <span key={a.key}>
                      {i > 0 && ", "}
                      <button
                        className="hover:text-foreground hover:underline transition"
                        onClick={() => { if (a.person) setSelectedPerson(a.person); }}
                      >
                        {a.displayName}
                      </button>
                    </span>
                  ));
                })()}
              </p>
            </SheetHeader>
            {/* Ask AI bar at top */}
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MessageSquare className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={aiQuestion}
                    onChange={e => setAiQuestion(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") handleAskAI();
                    }}
                    placeholder={activeTab === "recording" ? t("meetings.askPlaceholderRecording") : t("meetings.askPlaceholder")}
                    className="w-full pl-8 pr-10 py-2 text-sm rounded-md border border-input bg-card focus:outline-none focus:ring-2 focus:ring-ring/20"
                  />
                  {aiQuestion.trim() && (
                    <button onClick={handleAskAI} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-accent hover:opacity-80 transition">
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {activeTab === "recording" && (
                  <>
                    <button onClick={handleCopyTranscript} className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1 transition shrink-0">
                      <Copy className="h-3 w-3" />{t("meetings.copy")}
                    </button>
                    <MutationGate allowed={canShareMeeting} reason={READ_ONLY_MEETINGS_REASON}>
                    <button
                      disabled={isSharing || !canShareMeeting}
                      onClick={async () => {
                        if (!canShareMeeting) return;
                        if (isLovablePreview() || !workspaceId || !note?.id) {
                          const shareUrl = `${window.location.origin}/shared/meeting/${note?.id}`;
                          await navigator.clipboard.writeText(shareUrl);
                          toast.success(t("meetings.shareLinkCopied"));
                          return;
                        }
                        setIsSharing(true);
                        try {
                          const response = await shareEngagement({
                            variables: {
                              workspaceId: Number(workspaceId),
                              engagementId: note.id,
                              notifyOnView: false,
                              summaryTemplateId:
                                selectedTemplateId && selectedTemplateId !== "default"
                                  ? selectedTemplateId
                                  : undefined,
                            },
                          });
                          const slug = response.data?.shareEngagement?.slug;
                          if (slug) {
                            const slugToken = slug.includes("/") ? slug.split("/").filter(Boolean).pop() : slug;
                            const shareUrl = `${window.location.origin}/shared/meeting/${slugToken}`;
                            await navigator.clipboard.writeText(shareUrl);
                            toast.success(t("meetings.shareLinkCopied"));
                          } else {
                            toast.error(t("meetings.shareLinkFailed"));
                          }
                        } catch (err) {
                          console.error("Failed to share engagement", err);
                          toast.error(t("meetings.shareLinkFailed"));
                        } finally {
                          setIsSharing(false);
                        }
                      }}
                      className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1 transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    >
                      {isSharing ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Share2 className="h-3 w-3" />
                      )}
                      {isSharing ? t("meetings.generating") : t("sharing.share")}
                    </button>
                    </MutationGate>
                    {/* Group-based access control — separate concern from the
                        public share LINK button above. */}
                    {recordSharingEnabled && note?.id && (
                      <MutationGate>
                        <button
                          onClick={() => { if (canMutate) setShowManageAccess(true); }}
                          disabled={!canMutate}
                          className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1 transition"
                        >
                          <UsersIcon className="h-3 w-3" />
                          {t("sharing.manageAccess")}
                        </button>
                      </MutationGate>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <EngagementPrivacyGate engagementType="meeting" engagement={engagement} loading={engagementLoading && !isLovablePreview()} error={engagementError}>
          <div className="px-6 pb-6 pt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full grid grid-cols-5 mb-5">
                <TabsTrigger value="summary" className="text-[11px] gap-1"><Sparkles className="h-3 w-3" />{t("meetings.tabSummary")}</TabsTrigger>
                <TabsTrigger value="recording" className="text-[11px] gap-1"><Video className="h-3 w-3" />{t("meetings.tabRecording")}</TabsTrigger>
                <TabsTrigger value="notes" className="text-[11px] gap-1"><FileText className="h-3 w-3" />{t("meetings.tabNotes")}</TabsTrigger>
                <TabsTrigger value="email" className="text-[11px] gap-1"><Mail className="h-3 w-3" />{t("meetings.tabEmail")}</TabsTrigger>
                <TabsTrigger value="tasks" className="text-[11px] gap-1"><ListChecks className="h-3 w-3" />{t("meetings.tabTasks")}</TabsTrigger>
              </TabsList>

              {/* ═══ SUMMARY ═══ */}
              {isRecorded ? (
              <><TabsContent value="summary" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{t("meetings.summaryHeading")}{canEditSummary && (showTemplateSummary || showLegacySummary) && <span className="ml-1.5 text-[10px] font-normal opacity-70">{t("meetings.autoSaves")}</span>}</h3>
                  {(() => {
                    // In preview mode, derive templates from the same sticky
                    // state SettingsPage writes — so the admin's pick and any
                    // custom templates flow through here.
                    let templates: Array<{ id: string; name: string; isDefault?: boolean; builtIn?: boolean }> = [];
                    let adminPickId: string | null = null;
                    if (isLovablePreview()) {
                      const summaryOnly = (previewSavedTemplates || []).filter(t => t.type === "summary");
                      templates = summaryOnly.map(t => ({ id: t.id, name: t.name, isDefault: !!t.builtIn, builtIn: !!t.builtIn }));
                      adminPickId = previewSummaryTemplate && previewSummaryTemplate !== "general"
                        ? previewSummaryTemplate
                        : (templates.find(t => t.id === "general-summary")?.id ?? templates[0]?.id ?? null);
                    } else {
                      templates = (templatesData?.summaryTemplates?.edges?.map((edge) => edge?.node).filter(Boolean) ?? []) as any;
                      adminPickId = workspaceDefaultSummaryTemplateId ?? null;
                    }
                    // Only show the dropdown when the workspace has at least one
                    // user-authored custom template (non-builtin, non-seeded)
                    // beyond the master template chosen in workspace settings.
                    const customTemplates = templates.filter(
                      (t: any) => t && t.builtIn !== true && t.isDefault !== true
                    );
                    if (customTemplates.length === 0) return null;
                    const masterTemplate = adminPickId
                      ? templates.find((t: any) => t?.id === adminPickId)
                      : null;
                    const visibleTemplates = masterTemplate
                      ? [masterTemplate, ...customTemplates.filter((t: any) => t.id !== masterTemplate.id)]
                      : customTemplates;
                    return (
                      <Select
                        value={selectedTemplateId}
                        onValueChange={(value) => {
                          setSelectedTemplateId(value);
                          // Persist as the end user's personal default so the next
                          // meeting opens with the template they last chose.
                          if (!isPreviewMode && realCurrentUser?.id && workspaceId) {
                            updateUserMutation({
                              variables: {
                                workspaceId,
                                input: { id: realCurrentUser.id, defaultSummaryTemplateId: value },
                              },
                            }).catch((err) => {
                              console.error("Failed to persist default summary template", err);
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-[180px] h-8 text-xs">
                          <SelectValue placeholder={t("meetings.templatePlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {visibleTemplates.map((template) => {
                            if (!template) return null;
                            const isUserDefault = template.id === defaultTemplateId;
                            return (
                              <SelectItem key={template.id} value={template.id}>
                                {isUserDefault
                                  ? t("meetings.templateNameDefault", { name: template.name })
                                  : template.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>

                {summaryEditEnabled ? (
                  // Editable variant (flag on): keyed per engagement + template so a
                  // save never remounts it, and a different meeting/template always
                  // remounts with its own content. Read-only users can't edit.
                  showTemplateSummary ? (
                    <div className="meeting-summary-editor">
                      <NoteEditor
                        key={summaryTemplateEditKey ?? selectedTemplateId}
                        initialContent={editedTemplateSummaryBlocks ?? summaryBlocks}
                        onChange={handleTemplateSummaryChange}
                        autoFocus={false}
                        editable={canMutate}
                      />
                    </div>
                  ) : showLegacySummary ? (
                    <div className="meeting-summary-editor">
                      <NoteEditor
                        key={summaryLegacyEditKey ?? "summary-bullets"}
                        initialContent={editedLegacySummaryBlocks ?? summaryPointsToBlocks(actualSummaryPoints)}
                        onChange={handleLegacySummaryChange}
                        autoFocus={false}
                        editable={canMutate}
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border p-8 text-center">
                      <p className="text-muted-foreground text-xs">{t("meetings.noSummary")}</p>
                    </div>
                  )
                ) : summaryBlocks && summaryBlocks.length > 1 ? (
                  <div className="meeting-summary-editor">
                    <NoteEditor
                      key={selectedTemplateId}
                      initialContent={summaryBlocks}
                      onChange={() => { }}
                      autoFocus={false}
                    />
                  </div>
                ) : actualSummaryPoints.length > 0 ? (
                  <div className="meeting-summary-editor">
                    <NoteEditor
                      key="summary-bullets"
                      initialContent={summaryPointsToBlocks(actualSummaryPoints)}
                      onChange={() => { }}
                      autoFocus={false}
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border p-8 text-center">
                    <p className="text-muted-foreground text-xs">{t("meetings.noSummary")}</p>
                  </div>
                )}
              </TabsContent>

              {/* ═══ RECORDING + TRANSCRIPT ═══ */}
              <TabsContent value="recording" className="space-y-4">
                <MeetingTranscriptIncompleteNote
                  message={useBackendData ? engagement?.transcriptStatusMessage : undefined}
                  incomplete={useBackendData ? engagement?.transcriptIncomplete : undefined}
                />
                <div className="recording-layout space-y-4">
                <div className="recording-media-column space-y-4">
                {/* Video player */}
                {videoSrc ? (
                  <div className="rounded-lg overflow-hidden border border-border bg-black relative aspect-video">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover block"
                      preload="metadata"
                      onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onDurationChange={(e) => setVideoDuration(e.currentTarget.duration || 0)}
                      onLoadedMetadata={(e) => {
                        const v = e.currentTarget;
                        const d = v.duration;
                        setVideoDuration(d || 0);
                        if (d > 300) v.currentTime = 300;
                        else if (d > 240) v.currentTime = 240;
                        else if (d > 180) v.currentTime = 180;
                        else if (d > 120) v.currentTime = 120;
                        else if (d > 60) v.currentTime = 60;
                        else if (d > 30) v.currentTime = 30;
                        else if (d > 10) v.currentTime = 10;
                        else if (d > 5) v.currentTime = 5;
                      }}
                    >
                      <source src={localizeMediaUrl(videoSrc)} type="video/mp4" />
                      {t("meetings.videoUnsupported")}
                    </video>
                    {showVideoOverlay && (
                      <div
                        className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer"
                        onClick={togglePlay}
                      >
                        <Play className="h-12 w-12 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>
                ) : isLovablePreview() ? (
                  <div className="rounded-lg border border-border bg-black aspect-video relative overflow-hidden group cursor-pointer" onClick={() => setIsPlaying(!isPlaying)}>
                    <img src={zoomMeetingFrame} alt={t("meetings.recordingAlt")} className="w-full h-full object-cover" />
                    {/* Play/pause overlay */}
                    <div className={cn(
                      "absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity",
                      isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                    )}>
                      {isPlaying
                        ? <Pause className="h-12 w-12 text-white drop-shadow-lg" />
                        : <Play className="h-12 w-12 text-white drop-shadow-lg" />
                      }
                    </div>
                  </div>
                ) : null}

                {/* Custom playback controls (Gong-style) */}
                {videoSrc || isLovablePreview() ? (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 space-y-2">
                    {/* Scrubber */}
                    <div
                      className="relative h-1.5 rounded-full bg-muted cursor-pointer group"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pct = (e.clientX - rect.left) / rect.width;
                        if (videoDuration) seekTo(pct * videoDuration);
                      }}
                    >
                      {videoDuration > 0 &&
                        clips.map((c) => (
                          <div
                            key={c.id}
                            className="absolute top-0 h-full bg-accent/30 rounded-full"
                            style={{
                              left: `${(c.startSec / videoDuration) * 100}%`,
                              width: `${((c.endSec - c.startSec) / videoDuration) * 100}%`,
                            }}
                          />
                        ))}
                      <div
                        className="absolute top-0 left-0 h-full bg-accent rounded-full"
                        style={{ width: `${videoDuration ? (displayTime / videoDuration) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => skip(-15)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition text-[11px] font-medium"
                        title={t("meetings.back15Seconds")}
                      >
                        <RotateCcw className="h-4 w-4" />
                        <span>{t("meetings.skipSeconds", { seconds: 15 })}</span>
                      </button>
                      <button
                        onClick={togglePlay}
                        className="p-2 rounded-full bg-accent text-accent-foreground hover:opacity-90 transition"
                        title={isPlaying ? t("meetings.pause") : t("meetings.play")}
                      >
                        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => skip(15)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition text-[11px] font-medium"
                        title={t("meetings.forward15Seconds")}
                      >
                        <span>{t("meetings.skipSeconds", { seconds: 15 })}</span>
                        <RotateCw className="h-4 w-4" />
                      </button>
                      <span className="text-[11px] text-muted-foreground font-mono ml-2">
                        {fmtTime(displayTime)} / {fmtTime(videoDuration)}
                      </span>
                      <div className="ml-auto flex items-center gap-1">
                        <Popover open={speedMenuOpen} onOpenChange={setSpeedMenuOpen}>
                          <PopoverTrigger asChild>
                            <button
                              className="text-[11px] px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition font-medium"
                              title={t("meetings.playbackSpeed")}
                            >
                              {t("meetings.playbackRate", { rate: playbackRate })}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-28 p-1">
                            {PLAYBACK_RATES.map((r) => (
                              <button
                                key={r}
                                onClick={() => setRate(r)}
                                className={cn(
                                  "w-full flex items-center justify-between px-2 py-1.5 text-xs rounded hover:bg-muted transition",
                                  r === playbackRate && "font-semibold text-foreground",
                                )}
                              >
                                <span>{t("meetings.playbackRate", { rate: r })}</span>
                                {r === playbackRate && <Check className="h-3 w-3" />}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>
                        <button
                          onClick={toggleMute}
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
                          title={isMuted ? t("meetings.unmute") : t("meetings.mute")}
                        >
                          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </button>
                        <MutationGate allowed={canUseMeetingCapture} reason={READ_ONLY_MEETINGS_REASON}>
                          <button
                            onClick={createClipFromSelection}
                            disabled={!canUseMeetingCapture}
                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition font-medium"
                            title={t("meetings.clipTooltip")}
                          >
                            <Scissors className="h-3.5 w-3.5" />
                            {t("meetings.clip")}
                          </button>
                        </MutationGate>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Clips list */}
                {clips.length > 0 && (
                  <div className="rounded-lg border border-border bg-card p-3 space-y-2 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2">
                      <Scissors className="h-3.5 w-3.5 text-accent" />
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("meetings.clipsHeading", { count: clips.length })}
                      </h4>
                    </div>
                    <div className="space-y-1.5">
                      {clips.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/40 transition group min-w-0 w-full"
                        >
                          <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0">
                            {fmtTime(c.startSec)}–{fmtTime(c.endSec)}
                          </span>
                          {editingClipId === c.id ? (
                            <input
                              autoFocus
                              defaultValue={c.name}
                              onBlur={(e) => {
                                renameClip(c.id, e.target.value.trim() || c.name);
                                setEditingClipId(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                if (e.key === "Escape") setEditingClipId(null);
                              }}
                              className="flex-1 min-w-0 text-xs bg-background border border-input rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                          ) : (
                            <button
                              onClick={() => { if (canUseMeetingCapture) setEditingClipId(c.id); }}
                              className={cn(
                                "flex-1 min-w-0 text-left text-xs font-medium truncate transition",
                                canUseMeetingCapture ? "hover:text-accent" : "cursor-default",
                              )}
                              title={canUseMeetingCapture ? t("meetings.clickToRename") : undefined}
                            >
                              {c.name}
                            </button>
                          )}
                          {c.status === CLIP_STATUS.FAILED ? (
                            <span className="text-[10px] text-destructive px-1 shrink-0" title={c.statusError || t("meetings.clipFailedTooltip")}>{t("meetings.clipFailed")}</span>
                          ) : c.status !== CLIP_STATUS.READY ? (
                            <span className="text-[10px] text-muted-foreground px-1 shrink-0 inline-flex items-center gap-1" title={t("meetings.clipProcessingTooltip")}>
                              <Loader2 className="h-3 w-3 animate-spin" /> {t("meetings.clipProcessing")}
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => playClip(c)}
                                className="p-1 rounded hover:bg-muted transition text-muted-foreground hover:text-foreground"
                                title={t("meetings.playClip")}
                              >
                                <Play className="h-3 w-3" />
                              </button>
                              <MutationGate allowed={canUseMeetingCapture} reason={READ_ONLY_MEETINGS_REASON}>
                                <button
                                  onClick={() => copyClipLink(c)}
                                  disabled={!canUseMeetingCapture}
                                  className="p-1 rounded hover:bg-muted transition text-muted-foreground hover:text-foreground"
                                  title={t("meetings.copyShareLink")}
                                >
                                  <Share2 className="h-3 w-3" />
                                </button>
                              </MutationGate>
                            </>
                          )}
                          <MutationGate allowed={canUseMeetingCapture} reason={READ_ONLY_MEETINGS_REASON}>
                            <button
                              onClick={() => { if (canUseMeetingCapture) setConfirmDeleteClipId(c.id); }}
                              disabled={!canUseMeetingCapture}
                              className="p-1 rounded hover:bg-muted transition text-muted-foreground hover:text-destructive"
                              title={t("meetings.deleteClip")}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </MutationGate>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Speaker breakdown — color-coded */}
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{t("meetings.speakerBreakdown")}</h4>
                  <div className="space-y-3">
                    {speakers.map(speaker => {
                      const pct = totalTalk > 0 ? Math.round((speakerTalk[speaker] / totalTalk) * 100) : 0;
                      const colors = speakerColorMap[speaker];
                      return (
                        <div key={speaker}>
                          <div className="flex items-center gap-3 mb-1">
                            <UserAvatar name={speaker} size="xs" src={speakerAvatarMap[speaker] || undefined} />
                            <span className="text-xs font-medium w-24 truncate">{speaker}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{t("meetings.talkTime", { percent: pct })}</span>
                          </div>
                          {/* Timeline visualization — color-coded, clickable */}
                          <div
                            className="relative flex gap-0 h-2 ml-9 rounded-sm overflow-hidden cursor-pointer"
                            onMouseMove={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setBarHoverX(prev => ({ ...prev, [speaker]: e.clientX - rect.left }));
                            }}
                            onMouseLeave={() => setBarHoverX(prev => ({ ...prev, [speaker]: null }))}
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const percent = (e.clientX - rect.left) / rect.width;
                              const idx = Math.min(Math.floor(percent * actualTranscript.length), actualTranscript.length - 1);
                              if (actualTranscript[idx]?.timestamp !== undefined) handleSeekVideo(actualTranscript[idx].timestamp);
                            }}
                          >
                            {actualTranscript.map((seg, i) => {
                              const textLength = seg.text.length;
                              return (
                                <div
                                  key={i}
                                  style={{ width: `${textLength}px` }}
                                  className={cn(
                                    "h-full transition min-w-[2px]",
                                    seg.speaker === speaker ? colors.bg : "bg-muted"
                                  )}
                                />
                              );
                            })}
                            {barHoverX[speaker] != null && (
                              <div
                                className="absolute top-0 w-[3px] h-full rounded bg-foreground/60 shadow pointer-events-none"
                                style={{ left: barHoverX[speaker]! - 1 }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                </div>
                <div className="recording-transcript-column space-y-3">
                {/* Transcript */}
                <div
                  ref={transcriptScrollRef}
                  className="relative space-y-1 max-h-[320px] overflow-y-auto scrollbar-thin"
                  onMouseUp={showClipMenuFromSelection}
                >
                  {clipMenu && (
                    <div
                      className="absolute z-30 -translate-x-1/2 flex items-center gap-1 rounded-lg border border-border bg-popover px-1 py-0.5 shadow-lg"
                      style={{ top: clipMenu.top, left: clipMenu.left }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <button
                        onClick={() => { void createClipFromSelection(); setClipMenu(null); }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-muted text-foreground font-medium transition"
                        title={t("meetings.clipFromSelection")}
                      >
                        <Scissors className="h-3.5 w-3.5" /> {t("meetings.clip")}
                      </button>
                    </div>
                  )}
                  {filteredTranscript.map((t, i) => {
                    const colors = speakerColorMap[t.speaker];
                    const hasTimestamp = t.timestamp !== undefined && t.timestamp !== null;
                    const absoluteIdx = actualTranscript.indexOf(t);
                    const isActive = absoluteIdx === activeSegmentIndex;
                    const segStart = transcriptStarts[absoluteIdx] ?? 0;
                    const isInClip = clips.some(
                      (c) => segStart >= c.startSec && segStart < c.endSec,
                    );
                    return (
                      <div
                        key={i}
                        ref={isActive ? activeSegmentRef : undefined}
                        data-transcript-idx={absoluteIdx}
                        onClick={() => {
                          // Don't seek when user is selecting text
                          const sel = window.getSelection?.();
                          if (sel && sel.toString().length > 0) return;
                          if (hasTimestamp) {
                            handleSeekVideo(t.timestamp);
                          } else {
                            seekTo(segStart);
                            if (videoRef.current) videoRef.current.play();
                            else setIsPlaying(true);
                          }
                        }}
                        className={cn(
                          "flex gap-3 py-2 px-2 rounded transition",
                          "cursor-pointer",
                          isActive && "bg-accent/10 ring-1 ring-accent/40",
                          isInClip && !isActive && "bg-accent/5",
                          "hover:bg-muted/30",
                        )}
                      >
                        <span className="text-[10px] text-muted-foreground w-8 shrink-0 pt-0.5 font-mono">{t.time}</span>
                        <UserAvatar name={t.speaker} size="xs" src={speakerAvatarMap[t.speaker] || undefined} />
                        <div className="min-w-0">
                          <span className="text-xs font-medium">{t.speaker}</span>
                          <p className="text-xs text-muted-foreground leading-relaxed">{t.text}</p>
                        </div>
                      </div>
                    );
                  })}
                  {filteredTranscript.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-6">{t("meetings.noMatches")}</p>
                  )}
                </div>

                {/* Viewed by */}
                {showViewedBy && (
                  <div>
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("meetings.viewedBy")}</h4>
                    {viewers.length > 0 ? (
                      <div className="space-y-2">
                        {viewers.map(s => (
                          <div key={s.name} className="flex items-center gap-3 text-xs">
                            <UserAvatar name={s.name} size="xs" />
                            <span className="font-medium">{s.name}</span>
                            <span className="flex items-center gap-1 text-muted-foreground ml-auto"><Eye className="h-3 w-3" />{s.viewedAt}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t("meetings.noViewers")}</p>
                    )}
                  </div>
                )}
                </div>
                </div>
              </TabsContent>

              {/* ═══ NOTES ═══ */}
              <TabsContent value="notes" className="space-y-4">
                {/* Toggle between AI and Manual Notes */}
                <div className="flex items-center justify-between">
                  <div className="inline-flex rounded-lg border border-border bg-muted/30 p-0.5">
                    <button
                      onClick={() => setNotesMode("ai")}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                        notesMode === "ai"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Sparkles className="h-3 w-3" />
                      {t("meetings.aiNotes")}{notesMode === "ai" && canEditSummary && !!aiNote?.id && <> <span className="text-[10px] opacity-70">{t("meetings.autoSaves")}</span></>}
                    </button>
                    <button
                      onClick={() => setNotesMode("manual")}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                        notesMode === "manual"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Pencil className="h-3 w-3" />
                      {t("meetings.manualNotes")} {notesMode === "manual" && <span className="text-[10px] opacity-70">{t("meetings.autoSaves")}</span>}
                    </button>
                  </div>

                  {/* Copy button */}
                  {notesMode === "ai" && (isLovablePreview() ? combinedNotes.trim() : actualAiNotesBlocks) && (
                    <button
                      onClick={() => {
                        if (isLovablePreview()) {
                          navigator.clipboard.writeText(combinedNotes);
                        } else if (actualAiNotesBlocks) {
                          // Convert blocks to plain text for copying
                          const text = actualAiNotesBlocks.map(block => {
                            if (block.type === 'heading' && block.content) {
                              return block.content.map(c => (c as { text?: string }).text || '').join('');
                            }
                            if (block.type === 'bulletListItem' && block.content) {
                              return '• ' + block.content.map(c => (c as { text?: string }).text || '').join('');
                            }
                            if (block.type === 'paragraph' && block.content) {
                              return block.content.map(c => (c as { text?: string }).text || '').join('');
                            }
                            return '';
                          }).filter(line => line.trim()).join('\n');
                          navigator.clipboard.writeText(text);
                        }
                        toast.success(t("meetings.notesCopied"));
                      }}
                      className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1 transition"
                    >
                      <Copy className="h-3 w-3" />{t("meetings.copy")}
                    </button>
                  )}
                </div>

                {/* Source toggle — shown when AI notes exist from more than one source
                    (Coffee, Granola, Fireflies). */}
                {notesMode === "ai" && showNotesSourceToggle && (
                  <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5 mb-2">
                    <button
                      onClick={() => setAiNotesSource("coffee")}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                        effectiveSource === "coffee"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <img src="/logos/coffee-icon.svg" alt="" className="h-3 w-3 object-contain" />
                      Coffee
                    </button>
                    {notesSourceOptions.map(option => (
                      <button
                        key={option.source}
                        onClick={() => setAiNotesSource(option.source)}
                        className={cn(
                          "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                          effectiveSource === option.source
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <img src={option.logo} alt="" className="h-3 w-3 object-contain" />
                        {t(option.labelKey)}
                      </button>
                    ))}
                  </div>
                )}

                {/* AI Notes (editable) */}
                {notesMode === "ai" && (
                  isLovablePreview() ? (
                    combinedNotes.trim() ? (
                      <div className="rounded-lg border border-border bg-background">
                        <NoteEditor
                          key={`ai-notes-preview-${effectiveSource}`}
                          initialContent={markdownToBlocks(effectiveSource === "granola" ? granolaPreviewNotes : combinedNotes)}
                          onChange={() => { }}
                          autoFocus={false}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border p-8 text-center">
                        <p className="text-muted-foreground text-xs">{t("meetings.noAiNotes")}</p>
                      </div>
                    )
                  ) : summaryEditEnabled && (actualAiNotesBlocks || editedAiNotesBlocks) ? (
                    // Editable variant (flag on): keyed per note id (Coffee, Granola
                    // and Fireflies notes all save via their own note), never
                    // remounted by a save; read-only users can't edit.
                    <div className="rounded-lg border border-border bg-background">
                      <NoteEditor
                        key={aiNotesEditKey ?? `ai-notes-${effectiveSource}`}
                        initialContent={editedAiNotesBlocks ?? actualAiNotesBlocks}
                        onChange={handleAiNotesChange}
                        autoFocus={false}
                        editable={canMutate}
                      />
                    </div>
                  ) : actualAiNotesBlocks ? (
                    <div className="rounded-lg border border-border bg-background">
                      <NoteEditor
                        key={`ai-notes-${effectiveSource}`}
                        initialContent={actualAiNotesBlocks}
                        onChange={() => { }}
                        autoFocus={false}
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border p-8 text-center">
                      <p className="text-muted-foreground text-xs">{t("meetings.noAiNotes")}</p>
                    </div>
                  )
                )}

                {/* Manual Notes (editable) */}
                {notesMode === "manual" && (
                  <div className="rounded-lg border border-border bg-background">
                    <NoteEditor
                      initialContent={manualNoteContent}
                      onChange={handleManualNoteChange}
                      autoFocus={false}
                    />
                  </div>
                )}
              </TabsContent>

              {/* ═══ EMAIL ═══ */}
              <TabsContent value="email" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{t("meetings.followUpHeading")}</h3>
                  <button
                    onClick={() => { navigator.clipboard.writeText(effectiveFollowUpEmail); toast.success(t("meetings.emailCopied")); }}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-card text-muted-foreground hover:bg-secondary flex items-center gap-1 transition"
                  >
                    <Copy className="h-3 w-3" />{t("meetings.copy")}
                  </button>
                </div>
                <div className="rounded-lg border border-border overflow-hidden">
                  <div className="space-y-1.5 px-4 pt-4 pb-3 text-xs text-muted-foreground border-b border-border">
                    {(() => {
                      const currentUserEmail = realCurrentUser?.email?.toLowerCase();
                      if (useBackendData && engagement?.contacts) {
                        // External contacts go in To
                        const toNames = engagement.contacts
                          .map((c) => getDisplayName(c?.properties?.firstName, c?.properties?.lastName, c?.primaryEmail))
                          .filter(Boolean);
                        // Internal users (excluding current user) go in Cc
                        const ccNames = (engagement as any)?.users
                          ?.filter((u: any) => u?.email?.toLowerCase() !== currentUserEmail)
                          .map((u: any) => {
                            const name = [u.preferredName || u.givenName, u.surname].filter(Boolean).join(" ").trim();
                            return name || u.email || '';
                          })
                          .filter(Boolean) || [];
                        return (
                          <>
                            <p><span className="font-medium text-foreground">{t("meetings.emailTo")}</span> {toNames.join(", ") || "—"}</p>
                            {ccNames.length > 0 && (
                              <p><span className="font-medium text-foreground">{t("meetings.emailCc")}</span> {ccNames.join(", ")}</p>
                            )}
                          </>
                        );
                      }
                      // Preview/dummy mode: external people in To, workspace members in Cc
                      const externalNames = note.people.filter(name => !workspaceMembers.some(m => m.name === name) && name !== currentUser.name);
                      const internalNames = note.people.filter(name => (workspaceMembers.some(m => m.name === name) || name === currentUser.name) && name !== currentUser.name);
                      return (
                        <>
                          <p><span className="font-medium text-foreground">{t("meetings.emailTo")}</span> {externalNames.length > 0 ? externalNames.join(", ") : note.people.join(", ")}</p>
                          {internalNames.length > 0 && (
                            <p><span className="font-medium text-foreground">{t("meetings.emailCc")}</span> {internalNames.join(", ")}</p>
                          )}
                        </>
                      );
                    })()}
                    <p><span className="font-medium text-foreground">{t("meetings.emailSubject")}</span> {t("meetings.followUpSubject", { title: note.name })}</p>
                  </div>
                  {actualFollowUpEmail.trim() ? (
                    <NoteEditor
                      key={`email-editor-${note.id}-${followUpEditorRev}`}
                      initialContent={emailToBlocks(effectiveFollowUpEmail)}
                      onChange={(blocks) => handleFollowUpEditorChange(blocksToPlainText(blocks))}
                      autoFocus={false}
                    />
                  ) : (
                    <div className="p-4 text-muted-foreground text-xs">{t("meetings.noFollowUpEmail")}</div>
                  )}
                  {/^\s*<.+>/s.test(emailSignature) ? (
                    <div
                      className="px-4 py-3 border-t border-border text-xs text-muted-foreground [&_a]:underline [&_p]:m-0"
                      dangerouslySetInnerHTML={{ __html: emailSignature }}
                    />
                  ) : (
                    <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground whitespace-pre-line">
                      {emailSignature}
                    </div>
                  )}
                </div>
                {(() => {
                  let followUpTo: { id: string; name: string; email: string }[] = [];
                  let followUpCc: { id: string; name: string; email: string }[] = [];
                  if (useBackendData && engagement?.contacts) {
                    const { to, cc } = getFollowUpRecipientDetails(
                      engagement.contacts,
                      (engagement as any)?.users,
                      realCurrentUser?.email,
                    );
                    followUpTo = to.map(r => ({ id: r.email, name: r.name, email: r.email }));
                    followUpCc = cc.map(r => ({ id: r.email, name: r.name, email: r.email }));
                  } else {
                    // Preview/dummy mode
                    const emailMap = PREVIEW_ATTENDEE_EMAILS;
                    const externalPeople = (note.people || []).filter(name => name !== currentUser.name && !workspaceMembers.some(m => m.name === name));
                    const internalPeople = (note.people || []).filter(name => name !== currentUser.name && workspaceMembers.some(m => m.name === name));
                    followUpTo = externalPeople
                      .map(name => ({ id: emailMap[name] || "", name, email: emailMap[name] || "" }))
                      .filter(r => r.email);
                    followUpCc = internalPeople
                      .map(name => {
                        const member = workspaceMembers.find(m => m.name === name);
                        return { id: member?.email || "", name, email: member?.email || "" };
                      })
                      .filter(r => r.email);
                  }
                  return (
                    <SendFollowUpEmailButton
                      recipients={followUpTo}
                      ccRecipients={followUpCc}
                      subject={t("meetings.followUpSubject", { title: note.name })}
                      body={effectiveFollowUpEmail}
                      disabled={!actualFollowUpEmail.trim()}
                      draftKey={followUpDraftKey ?? undefined}
                      onSaveDraft={handleFollowUpDraftSaved}
                    />
                  );
                })()}
              </TabsContent>

              {/* ═══ TASKS ═══ */}
              <TabsContent value="tasks" className="space-y-4">
                <h3 className="text-sm font-semibold">{t("meetings.suggestedTasks")}</h3>
                <div className="space-y-2">
                  {actualSuggestedTasks.filter(t => !addedTaskIds.has(t.id)).map(task => {
                    const isStriking = strikingTaskIds.has(task.id);
                    const checked = isStriking || (selectedTasks[task.id] ?? false);

                    return (
                      <div key={task.id} className={cn(
                        "rounded-lg border border-border bg-card p-3 flex items-center gap-3 transition hover:ring-1 hover:ring-accent/30 group",
                        isStriking && "opacity-50 transition-opacity duration-500"
                      )}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => !isStriking && setSelectedTasks(prev => ({ ...prev, [task.id]: !!v }))}
                          className="h-4 w-4 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          disabled={isStriking}
                        />
                        <button
                          className={cn(
                            "text-sm font-semibold flex-1 text-left bg-transparent border-none focus:outline-none rounded px-1 -mx-1 hover:text-accent transition truncate",
                            isStriking && "line-through text-muted-foreground"
                          )}
                          title={t("meetings.openTaskDetails")}
                          disabled={isStriking}
                          onClick={() => {
                            if (isLovablePreview()) {
                              upsertTask({
                                id: task.id,
                                title: task.title,
                                priority: task.priority,
                                dueDate: format(defaultDueDate, "MMM d, yyyy"),
                                assignee: task.assignee || currentUser.name,
                                completed: false,
                              });
                            }
                            setTaskDetailId(task.id);
                          }}
                        >
                          {task.title}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={async () => {
                    if (isAddingTasks) return;
                    const selected = actualSuggestedTasks.filter(t => selectedTasks[t.id] && !addedTaskIds.has(t.id));
                    if (selected.length === 0) return;
                    const addedIds = new Set(selected.map(t => t.id));

                    // Optimistic feedback: strike, clear selection, confirm — all before
                    // the (slow) backend calls, so the click registers instantly and can't
                    // be mistaken for a no-op. Guard against double-submits with isAddingTasks.
                    setIsAddingTasks(true);
                    setStrikingTaskIds(prev => new Set([...prev, ...addedIds]));
                    setSelectedTasks({});
                    toast.success(t("meetings.tasksCreated", { count: selected.length }));

                    // Create all selected tasks in backend with original values (not edited).
                    // Suppress per-task toasts — we showed a single summary toast above.
                    try {
                      await Promise.all(selected.map(t => createSuggestedTask({
                        title: t.title,
                        priority: t.priority,
                        dueDate: defaultDueDate,
                        silent: true,
                      })));
                      // On success, let the strikethrough animate, then drop the rows.
                      setTimeout(() => {
                        setAddedTaskIds(prev => new Set([...prev, ...addedIds]));
                        setStrikingTaskIds(prev => {
                          const next = new Set(prev);
                          addedIds.forEach(id => next.delete(id));
                          return next;
                        });
                      }, 1200);
                    } catch {
                      // Roll back the optimistic strike so the user can retry.
                      setStrikingTaskIds(prev => {
                        const next = new Set(prev);
                        addedIds.forEach(id => next.delete(id));
                        return next;
                      });
                      toast.error(t("meetings.tasksCreateFailed", { count: selected.length }));
                    } finally {
                      setIsAddingTasks(false);
                    }
                  }}
                  disabled={selectedCount === 0 || isAddingTasks}
                  className="text-sm px-4 py-2 rounded-md bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isAddingTasks
                    ? t("meetings.addingTasks")
                    : selectedCount > 0
                      ? t("meetings.addToTasksCount", { count: selectedCount })
                      : t("meetings.addToTasks")}
                </button>
              </TabsContent></>
              ) : (
                <MeetingNotRecorded
                  message={useBackendData ? engagement?.transcriptStatusMessage : undefined}
                />
              )}
            </Tabs>
          </div>
          </EngagementPrivacyGate>
        </SheetContent>
      </Sheet>
      {note?.id && (
        <ShareRecordDialog
          open={showManageAccess && canMutate}
          onOpenChange={setShowManageAccess}
          recordId={String(note.id)}
          recordName={note.name || note.displayName || ""}
          recordType="engagement"
        />
      )}
      <TaskDetailSheet
        taskId={taskDetailId}
        open={!!taskDetailId}
        onOpenChange={(open) => { if (!open) setTaskDetailId(null); }}
        hideOverlay={true}
        onBack={() => setTaskDetailId(null)}
      />
      <PersonDetailSheet
        open={!!selectedPerson}
        onOpenChange={(open) => { if (!open) setSelectedPerson(null); }}
        person={selectedPerson}
        hideOverlay={true}
        onBack={() => setSelectedPerson(null)}
      />
      <CompanyDetailSheet
        open={!!selectedCompany}
        onOpenChange={(open) => { if (!open) setSelectedCompany(null); }}
        company={selectedCompany}
        hideOverlay={true}
        onBack={() => setSelectedCompany(null)}
      />
      <AskCoffeePanel
        open={askCoffeePanelOpen}
        onOpenChange={(open) => {
          setAskCoffeePanelOpen(open);
          if (!open) {
            // Defer clearing query to allow panel to fully unmount
            setTimeout(() => setAskCoffeeQuery(""), 100);
          }
        }}
        initialQuery={askCoffeeQuery}
        initialContext={useBackendData && engagement?.id ? { type: "meeting" as const, id: engagement.id } : undefined}
        contextTitle={note?.name}
        contextSubtitle={note?.date ? `${note.date}${note.company ? ` · ${note.company}` : ''}` : undefined}
        onBack={() => setAskCoffeePanelOpen(false)}
      />
      <ConfirmDeleteDialog
        open={!!confirmDeleteClipId}
        onOpenChange={(open) => { if (!open) setConfirmDeleteClipId(null); }}
        onConfirm={() => { if (confirmDeleteClipId) deleteClip(confirmDeleteClipId); }}
        title={t("meetings.deleteClipTitle")}
        description={t("meetings.deleteClipDescription")}
      />
    </>
  );
}
